import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import { colors, rgbaFromHex } from '@utils/colorSystem';
import { SprintBoardColumn, KANBAN_DESKTOP_COLUMN_MIN_PX } from '@/pages/inside/modules/projects/detail/projectTabStyles';

const TENANT_TOOLBAR_ACCENT = '#7543DC';

export const KPICardsGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ $columns }) => {
        if ($columns === 4) {
            return 'repeat(4, 1fr)';
        }
        return $columns ? `repeat(${$columns}, 1fr)` : 'repeat(2, 1fr)';
    }};
    grid-template-rows: ${({ $rows }) => ($rows ? `repeat(${$rows}, 1fr)` : 'auto')};
    gap: 16px;
    width: 100%;
    height: ${({ $height }) => $height || '450px'};
    align-self: stretch;

    & > * {
        height: ${({ $height }) => ($height === 'auto' ? 'auto' : '100%')};
        min-height: 0;
        overflow: hidden;
    }

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        height: auto;
        gap: 12px;

        & > * {
            height: auto;
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 10px;
    }
`;

export const RolesSectionTitle = styled.h1`
    font-size: 24px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    margin: 0;
    font-family: var(--font-family-geist);
`;

export const RolesSectionSubtitle = styled.p`
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
    margin: 0;
`;

export const RolesSliderTabWrap = styled.div`
    width: fit-content;
`;

/** Wraps a `SliderTab` row so the tab strip spans the full width of its parent (e.g. modal body). */
export const SliderTabStripScroll = styled.div`
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;

    & > * {
        width: 100%;
        min-width: 0;
    }
`;

export const RolesSearchInput = styled.input`
    width: 343px;
    height: 44px;
    border-radius: 8px;
    padding: 0 16px 0 40px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#E5E5E5')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#F0F0F0')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-size: 14px;
    font-family: var(--font-family-geist);
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    }

    &:focus {
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#595959' : '#d9d9d9')};
    }

    @media (max-width: 768px) {
        width: 200px;
        height: 40px;
    }
`;

export const RolesSearchWrapper = styled.div`
    position: relative;
    display: inline-block;

    .anticon-search {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 16px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#8c8c8c')};
        pointer-events: none;
    }
`;

/** Full-width permission search (profile modal). */
export const ProfilePermSearchWrapper = styled(RolesSearchWrapper)`
    display: block;
    width: 100%;
`;

export const ProfilePermSearchInput = styled(RolesSearchInput)`
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

export const ProfilePageGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.72fr) minmax(0, 1.28fr);
    gap: 24px;
    width: 100%;
    align-items: stretch;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
`;

export const ProfilePageColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 100%;
`;

/** Account card — stretches to match right column (permissions + security). */
export const ProfileAccountCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding: 0;
    border-radius: 12px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    overflow: hidden;
`;

export const ProfileAccountCardBody = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

export const ProfileAccountCardSpacer = styled.div`
    flex: 1;
    min-height: 12px;
`;

/** Security card — grows so column bottoms align with account card. */
export const ProfileSecurityCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;
    border-radius: 12px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    overflow: hidden;
`;

export const ProfileSecurityCardFooter = styled.div`
    margin-top: auto;
`;

/** Security card password rows — fixed label column so all inputs share equal width. */
export const ProfilePasswordFieldsSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const ProfilePasswordFieldRow = styled.div`
    display: grid;
    grid-template-columns: 152px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f5f5f5')};

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 8px;
    }
`;

export const ProfilePasswordInputWrap = styled.div`
    width: 100%;
    min-width: 0;
`;

export const ProfileIdentityRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 24px;
    padding: 24px 20px;

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }
`;

export const ProfileAvatarButton = styled.button`
    position: relative;
    flex-shrink: 0;
    width: 96px;
    height: 96px;
    padding: 0;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    background: transparent;
    overflow: visible;

    &:disabled {
        cursor: wait;
        opacity: 0.85;
    }

    &:not(:disabled):hover .profile-avatar-overlay {
        opacity: 1;
    }
`;

export const ProfileAvatarImage = styled.div`
    width: 96px;
    height: 96px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#fff')};
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.08);
    background: ${({ $bg }) => $bg || '#7543DC'};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    font-weight: 700;
    font-family: var(--font-family-geist);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export const ProfileAvatarOverlay = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
`;

export const ProfileIdentityMeta = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const ProfileDisplayName = styled.h2`
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
    font-family: var(--font-family-geist);
    line-height: 1.3;
`;

export const ProfileMetaLine = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#666')};
    font-family: var(--font-family-geist);
`;

export const ProfileStatPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f5f3ff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#e9d5ff' : '#5b21b6')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#e9d5ff')};
`;

/** Workspace role badge on account header (distinct from plan/permission pills). */
export const ProfileRoleBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    font-family: var(--font-family-geist);
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1e3a5f' : '#eff6ff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#93c5fd' : '#1d4ed8')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2563eb' : '#bfdbfe')};
`;

export const ProfileIdFieldsSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const ProfileIdFieldRow = styled.div`
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f5f5f5')};

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 8px;
    }
`;

export const ProfileIdValueBox = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#141414' : '#f8fafc')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#e2e8f0')};
`;

export const ProfileIdValueText = styled.code`
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#e2e8f0' : '#334155')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ProfileIdCopyButton = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#93c5fd' : '#7543dc')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#fff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#e2e8f0')};
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#333' : '#f5f3ff')};
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#595959' : '#c4b5fd')};
    }
`;

export const ProfileStatsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 8px;
`;

export const RolesFilterBtn = styled.button`
    position: relative;
    height: 44px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#E5E5E5')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#F0F0F0')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-size: 14px;
    font-weight: 500;
    font-family: var(--font-family-geist);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s;
    overflow: visible;

    &:hover {
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#595959' : '#d9d9d9')};
    }
`;

/** Icon-only toolbar control (same chrome as RolesFilterBtn): refresh / filter next to search. */
export const RolesToolbarIconBtn = styled(RolesFilterBtn)`
    width: 44px;
    min-width: 44px;
    padding: 0;
    justify-content: center;
    gap: 0;

    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
`;

/** Matches `SliderTab` track min-height — search / Filters / refresh row. */
const TENANT_TOOLBAR_H = '40px';

const tenantToolbarRefreshSpin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

/** White field + light border (light mode); compact row next to segmented tabs. */
export const TenantToolbarSearchWrap = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    max-width: min(420px, 46vw);
    height: ${TENANT_TOOLBAR_H};
    box-sizing: border-box;

    .tenant-toolbar-search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
        pointer-events: none;
        z-index: 1;
        transition: color 0.2s ease;
    }

    &:has(input:hover) .tenant-toolbar-search-icon,
    &:has(input:focus-visible) .tenant-toolbar-search-icon,
    &:focus-within .tenant-toolbar-search-icon {
        color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : TENANT_TOOLBAR_ACCENT};
    }
`;

export const TenantToolbarSearchInput = styled.input`
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.12)' : '#e5e5e5')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1a1a1a' : '#ffffff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#171717')};
    font-size: 14px;
    font-family: var(--font-family-geist);
    outline: none;
    padding: 0 14px 0 38px;
    transition:
        border-color 0.2s ease,
        background-color 0.2s ease,
        transform 0.2s ease;

    &::placeholder {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
    }

    &:hover {
        border-color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.24)' : rgbaFromHex(TENANT_TOOLBAR_ACCENT, 0.48)};
        box-shadow: none;
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#222' : '#f6f2ff')};
        transform: translateY(-1px);
    }

    &:focus,
    &:focus-visible {
        border-color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.38)' : TENANT_TOOLBAR_ACCENT};
        box-shadow: none;
        outline: none;
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1c1c1c' : '#ffffff')};
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

export const TenantToolbarFilterBtn = styled.button`
    position: relative;
    box-sizing: border-box;
    height: ${TENANT_TOOLBAR_H};
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.12)' : '#e5e5e5')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1a1a1a' : '#ffffff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#171717')};
    font-size: 14px;
    font-weight: 500;
    font-family: var(--font-family-geist);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        background 0.2s ease,
        transform 0.2s ease,
        color 0.2s ease;

    .anticon {
        transition: color 0.2s ease, transform 0.2s ease;
    }

    &:hover {
        border-color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.32)' : rgbaFromHex(TENANT_TOOLBAR_ACCENT, 0.55)};
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f3edff')};
        box-shadow: none;
        transform: translateY(-1px);
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : TENANT_TOOLBAR_ACCENT)};
    }

    &:hover .anticon {
        color: inherit;
        transform: scale(1.05);
    }

    &:hover .anticon svg {
        fill: currentColor;
    }

    &:active {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.35)' : TENANT_TOOLBAR_ACCENT)};
        outline-offset: 2px;
    }
`;

/** Wraps toolbar filter control + optional clear badge (top-right). */
export const TenantToolbarFilterBtnOuter = styled.span`
    position: relative;
    display: inline-flex;
    vertical-align: middle;
`;

/** Small dismiss control when filters are applied (clears applied filters). */
export const TenantToolbarFilterClearBadge = styled.button`
    position: absolute;
    top: -5px;
    right: -5px;
    z-index: 2;
    width: 18px;
    height: 18px;
    padding: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    border: 1px solid
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.2)' : '#e5e5e5')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#ffffff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#404040')};
    cursor: pointer;
    box-shadow: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#333333' : '#f5f5f5')};
        border-color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.35)' : TENANT_TOOLBAR_ACCENT};
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : TENANT_TOOLBAR_ACCENT)};
        box-shadow: none;
    }

    .anticon {
        font-size: 10px;
        line-height: 1;
    }
`;

export const TenantToolbarIconBtn = styled.button`
    position: relative;
    box-sizing: border-box;
    height: ${TENANT_TOOLBAR_H};
    width: ${TENANT_TOOLBAR_H};
    min-width: ${TENANT_TOOLBAR_H};
    padding: 0;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.12)' : '#e5e5e5')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1a1a1a' : '#ffffff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#404040')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        background 0.2s ease,
        transform 0.2s ease,
        color 0.2s ease;

    svg {
        transition: transform 0.2s ease, color 0.2s ease;
    }

    &:hover:not(:disabled) {
        border-color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.32)' : rgbaFromHex(TENANT_TOOLBAR_ACCENT, 0.55)};
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f3edff')};
        box-shadow: none;
        transform: translateY(-1px);
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : TENANT_TOOLBAR_ACCENT)};
    }

    &:hover:not(:disabled):not([aria-busy='true']) svg {
        transform: scale(1.06) rotate(-6deg);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.35)' : TENANT_TOOLBAR_ACCENT)};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

export const TenantToolbarRefreshSpin = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: inherit;

    ${({ $spin }) =>
        $spin &&
        css`
            animation: ${tenantToolbarRefreshSpin} 0.88s linear infinite;
        `}
`;

export const DetailLabel = styled.span`
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
    font-family: var(--font-family-geist);
`;
export const DetailValue = styled.span`
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-family: var(--font-family-geist);
`;
export const DetailValueFlex = styled(DetailValue)`
    flex: 1;
`;
export const DetailSecondaryText = styled.span`
    opacity: 0.65;
`;
export const DetailBadge = styled.span`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 9999px;
    font-size: 13px;
    font-weight: 500;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
`;
export const DetailPermList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
export const DetailPermItem = styled.li`
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-family: var(--font-family-geist);
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fafafa')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#333')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
`;

/** Wrapped row of permission chips (short width, flex-wrap) */
export const DetailPermListWrapped = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;
export const DetailPermChip = styled.li`
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-family: var(--font-family-geist);
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fafafa')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#333')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    width: max-content;
    max-width: 100%;
`;

export const CreateRoleModalBody = styled.div`
    max-height: none !important;
    overflow-y: visible !important;
    overflow-x: hidden !important;
    padding: ${({ $flush }) => ($flush ? '0' : '4px 0')};
    width: 100%;
    box-sizing: border-box;

    .ant-input:not(textarea),
    .ant-input-affix-wrapper,
    .ant-select:not(.ant-select-multiple) .ant-select-selector,
    .ant-input-number,
    .ant-picker {
        border-radius: 8px;
        height: 44px !important;
        min-height: 44px !important;
    }

    .ant-input-number {
        display: inline-flex !important;
        align-items: center !important;
    }
    .ant-input-number-input-wrap {
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        width: 100% !important;
    }
    .ant-input-number-input {
        height: 44px !important;
        line-height: 44px !important;
        padding: 0 11px !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
    }

    .ant-select-multiple .ant-select-selector {
        border-radius: 8px;
        min-height: 44px !important;
        height: auto !important;
    }
    textarea.ant-input {
        border-radius: 8px;
    }
    .ant-input-number,
    .ant-picker {
        width: 100%;
    }
    .ant-select:not(.ant-select-multiple) {
        height: 44px !important;
    }
    .ant-select-multiple {
        min-height: 44px !important;
        height: auto !important;
    }
    .ant-input-affix-wrapper .ant-input {
        height: auto !important;
        min-height: auto !important;
    }
`;

/** Uppercase meta line above modal title (e.g. "Sprint", "Task") — generic shell */
export const ModalChromeEyebrow = styled.div`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
    margin-bottom: 2px;
    font-family: var(--font-family-geist);
`;

/** Primary line in custom modal title area */
export const ModalChromeHeading = styled.div`
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    font-family: var(--font-family-geist);
`;

/** Non-uppercase label for form fields in modals (e.g. “Description”) */
export const ModalBlockLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.88)' : '#374151')};
    font-family: var(--font-family-geist);
`;

/** Small section heading inside modals (e.g. "Client info", "Project info") */
export const ModalSectionHeading = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    margin-bottom: 8px;
    font-family: var(--font-family-geist);
`;

/** Block of read-only info text (e.g. client name and email) */
export const ModalInfoBlock = styled.div`
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#333')};
    margin-bottom: 20px;
    padding: 12px 0;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    font-family: var(--font-family-geist);
`;
export const CreateRolePermSection = styled.div`
    margin-top: 24px;
    margin-bottom: 12px;
`;
/** Keeps permission label + tabs + search sticky at top when scrolling modal body */
export const StickyPermHeader = styled.div`
    position: sticky;
    top: -15px;
    z-index: 1;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.default : '#fff'};
    padding-bottom: 12px;
    margin-bottom: 0;
`;
export const CreateRolePermLabel = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    margin-bottom: 12px;
    font-family: var(--font-family-geist);
`;
export const CreateRolePermToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
`;
/** Horizontal scroll for permission module tabs only; prevents horizontal scroll on table */
export const TabsScrollWrap = styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    min-width: 0;
    max-width: 100%;
    margin: 0 -2px;
    padding: 0 2px;

    &::-webkit-scrollbar {
        height: 6px;
    }
    &::-webkit-scrollbar-thumb {
        border-radius: 3px;
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#444' : '#d9d9d9')};
    }
`;
export const ThemedAntTableWrap = styled.div`
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#E5E5E5')};
    border-radius: 12px;
    overflow: hidden;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};

    .ant-table-wrapper {
        border-radius: 10px;
        overflow: hidden;
    }
    .ant-table {
        background: transparent !important;
        font-size: 13px;
    }
    .ant-table-thead > tr > th {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? '#2C2C2C' : '#fafafa'} !important;
        color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#000'} !important;
        font-weight: 600;
        font-size: 13px;
        font-family: var(--font-family-geist);
        border-bottom: 1px solid
            ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    }
    .ant-table-tbody > tr > td {
        font-size: 13px;
        font-family: var(--font-family-geist);
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#333')};
    }
    .ant-table-cell {
        padding: 12px 16px !important;
    }
    .ant-table-tbody > tr {
        cursor: pointer;
    }
    .ant-table-tbody > tr:hover > td {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? '#2C2C2C' : '#fafafa'} !important;
    }
`;
export const CreateRoleModalFooter = styled.div`
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    ${({ $inFooterSlot, theme }) =>
        $inFooterSlot
            ? `
        margin: 0;
        padding: 0;
        border-top: none;
    `
            : `
        margin-top: 0;
        padding-top: 12px;
        padding-bottom: 0;
        border-top: 1px solid ${theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0'};
    `}
`;

/** Full-width form control wrapper (e.g. for DatePicker/Select in modals). */
export const FormControlFullWidth = styled.div`
    width: 100%;
    .ant-picker {
        width: 100%;
    }
`;

export const ContentCard = styled.div`
    padding: ${({ $padding }) => $padding || '16px'};
    border-radius: 12px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    margin-bottom: ${({ $marginBottom }) => $marginBottom ?? '12px'};
    overflow: ${({ $overflow }) => $overflow || 'visible'};
    &:last-child {
        margin-bottom: 0;
    }
`;

export const ContentCardHeader = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
`;

export const ContentCardTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    margin: 0 0 ${({ $marginBottom }) => $marginBottom ?? 0}px 0;
    font-family: var(--font-family-geist);
`;

export const ContentCardRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f5f5f5')};
    &:last-child {
        border-bottom: none;
    }
`;

export const ContentCardFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 16px 20px;
    gap: 12px;
    border-top: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
`;

/** Two-column grid for dashboard or any equal-width panels. Stacks on small screens. */
export const TwoColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    width: 100%;
    align-items: start;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

/** Scrollable grid of equal-height cards (e.g. list items as cards). Rows with vertical scroll. */
export const CardGridScroll = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    overflow-y: auto;
    min-height: 0;
    flex: 1;
    align-items: stretch;
    grid-auto-rows: 1fr;
    align-content: start;
    padding: 12px;
    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

/** Scrollable card grid that goes 3 → 2 → 1 columns at explicit breakpoints. */
export const ThreeTwoOneCardGridScroll = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    overflow-y: auto;
    min-height: 0;
    flex: 1;
    align-items: stretch;
    align-content: start;
    padding: 12px;

    @media (max-width: 1600px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 1200px) {
        grid-template-columns: 1fr;
    }
`;

/** ContentCard with a minimum height (for grid layouts). Hover: shadow only (fill color unchanged). */
export const CardWithMinHeight = styled(ContentCard)`
    min-height: ${({ $minHeight }) => $minHeight ?? '200px'};
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-bottom: 0;
    transition: box-shadow 0.2s ease;
    &:hover {
        box-shadow: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? '0 6px 16px rgba(0,0,0,0.4)'
            : '0 6px 16px rgba(0,0,0,0.12)'};
    }
`;

/** Dashboard: no extra page padding — same 30px inset as other routes from `InsideBodyScrollInner`. */
export const DashboardPageWrap = styled.div`
    padding: 0;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 0;
    overflow-x: hidden;
`;

/** Main dashboard grid: top section (45% left + 55% right, then 50-50 bottom). */
export const DashboardMainGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${({ $gap }) => $gap ?? '24px'};
    width: 100%;
    min-width: 0;
    align-items: start;
    @media (max-width: 768px) {
        gap: 16px;
    }
`;

/** Top block: analytics (row1) + pie | graph (row2); notifications (spans 2 rows). Small screens: strict single column, one component per line. */
export const DashboardTopGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 20%) minmax(0, 25%) minmax(0, 55%);
    grid-template-rows: auto auto;
    grid-auto-flow: row;
    gap: 24px;
    width: 100%;
    min-width: 0;
    align-items: stretch;
    min-height: 320px;
    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        grid-template-rows: auto auto auto auto;
        grid-auto-flow: row;
        min-height: 0;
        gap: 16px;
    }
    @media (max-width: 768px) {
        gap: 12px;
    }
    & > * {
        @media (max-width: 1024px) {
            min-width: 0;
            max-width: 100%;
        }
    }
`;
export const DashboardAnalyticsSlot = styled.div`
    grid-column: 1 / span 2;
    grid-row: 1;
    min-width: 0;
    @media (max-width: 1024px) {
        grid-column: 1;
        grid-row: 1;
        width: 100%;
    }
`;
export const DashboardNotificationsSlot = styled.div`
    grid-column: 3;
    grid-row: 1 / span 2;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding-right: 50px;
    @media (max-width: 1024px) {
        grid-column: 1;
        grid-row: 2;
        width: 100%;
        padding-right: 0;
    }
`;
export const DashboardPieSlot = styled.div`
    grid-column: 1;
    grid-row: 2;
    min-width: 0;
    @media (max-width: 1024px) {
        grid-column: 1;
        grid-row: 3;
        width: 100%;
    }
`;
export const DashboardGraphSlot = styled.div`
    grid-column: 2;
    grid-row: 2;
    min-width: 0;
    @media (max-width: 1024px) {
        grid-column: 1;
        grid-row: 4;
        width: 100%;
    }
`;

/** Bottom block: recent activity (35%) | recent projects (65%). Small screens: single column, one per line, full width. */
export const DashboardBottomSplit = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 35%) minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    min-width: 0;
    align-items: stretch;
    & > * {
        min-width: 0;
        min-height: 0;
    }
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 16px;
        & > * {
            width: 100%;
            max-width: 100%;
        }
    }
`;

/** Dashboard section: no card/radius, direct content (e.g. Recent projects with table). */
export const DashboardSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    min-height: 0;
    height: 100%;
`;

/** Header row for dashboard section (title + optional action). */
export const DashboardSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding-bottom: 12px;
    margin-bottom: 0;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
`;

/** Dashboard card: same as ContentCard but square (no border-radius). */
export const DashboardCard = styled(ContentCard)`
    border-radius: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

/** Dashboard recent projects table: name wraps, progress bar has room. */
export const DashboardProjectsTableWrap = styled.div`
    width: 100%;
    min-width: 0;
    overflow-x: auto;
    .dashboard-project-name {
        white-space: normal;
        word-break: break-word;
        max-width: 180px;
        line-height: 1.4;
        font-weight: 500;
    }
    .dashboard-project-progress {
        min-width: 100px;
        max-width: 200px;
    }
    @media (max-width: 768px) {
        .dashboard-project-name {
            max-width: none;
        }
        .dashboard-project-progress {
            min-width: 80px;
            max-width: 140px;
        }
    }
`;

/** One card with 4–6 stats in 2 lines, divided by dividers. */
export const StatsCard = styled(ContentCard)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr 1fr;
    gap: 0;
    padding: 0;
    overflow: hidden;
    @media (min-width: 769px) {
        &.stats-cols-3 {
            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: 1fr;
        }
    }
`;
export const StatsCell = styled.div`
    padding: 20px 24px;
    @media (max-width: 768px) {
        padding: 16px 20px;
    }
    @media (max-width: 480px) {
        padding: 14px 16px;
    }
    border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    &:nth-child(2n) {
        border-right: none;
    }
    &:nth-child(n + 3) {
        border-bottom: none;
    }
    .stats-cols-3 &:nth-child(2n) {
        border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    }
    .stats-cols-3 &:nth-child(3n) {
        border-right: none;
    }
    .stats-cols-3 &:nth-child(n + 4) {
        border-bottom: none;
    }
    .stats-cols-3 &:nth-child(1),
    .stats-cols-3 &:nth-child(2),
    .stats-cols-3 &:nth-child(3) {
        border-bottom: none;
    }
    @media (max-width: 768px) {
        border-right: none;
        border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
        &:last-child {
            border-bottom: none;
        }
    }
`;
export const StatsCellValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    font-family: var(--font-family-geist);
    @media (max-width: 768px) {
        font-size: 20px;
    }
    @media (max-width: 480px) {
        font-size: 18px;
    }
`;
export const StatsCellLabel = styled.div`
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#666')};
    margin-top: 4px;
    font-family: var(--font-family-geist);
    @media (max-width: 480px) {
        font-size: 12px;
    }
`;

/** Row with unequal columns: first area narrower, second wider (e.g. pie | notifications). */
export const DashboardUnevenRow = styled.div`
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 24px;
    width: 100%;
    align-items: start;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export {
    ChartCard,
    ChartCardTitle,
    ChartCardSubtitle,
    ChartCardHeader,
    ChartCardBody,
} from './chartCardStyles';

/** Details tab: vertical layout with top row (75%) and bottom attachments section. */
export const DetailsTabLayout = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;
/** Top row: takes remaining height (above 15% attachments). */
export const DetailsTabTopRow = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    overflow: hidden;
`;
/** Bottom section for attachments: fixed 15% height. */
export const DetailsTabBottomSection = styled.div`
    flex: 0 0 25%;
    min-height: 0;
    overflow: auto;
    border-top: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
    padding-top: 16px;
    margin-top: 16px;
`;

/** Two-column split: left sidebar + right main content. Generic use (e.g. discussion, detail + chat). Use $ratio for equal split (e.g. 0.5 = 50%). Use $divider to show a vertical divider between columns. On small screen (768px) becomes single column when $responsive. */
export const SplitLayoutWrap = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    gap: ${({ $divider }) => ($divider ? 0 : '20px')};
    ${({ $responsive }) =>
        $responsive &&
        `
    @media (max-width: 768px) {
        flex-direction: column;
    }
    `}
`;
export const SplitSidebar = styled.div`
    border-right: ${({ $divider, theme }) => {
        if (!$divider) return 'none';
        const isDark = theme?.colors?.background?.default === '#141414';
        return `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.25)'}`;
    }};
    padding-right: ${({ $divider }) => ($divider ? '20px' : 0)};
    width: ${({ $width, $ratio }) => ($ratio != null ? undefined : $width ?? '320px')};
    min-width: ${({ $minWidth, $ratio }) => ($ratio != null ? 0 : $minWidth ?? '260px')};
    flex: ${({ $ratio }) => ($ratio != null ? `${$ratio} 1 0` : '0 0 auto')};
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    ${({ $responsive }) =>
        $responsive &&
        `
    @media (max-width: 768px) {
        display: contents;
        border-right: none;
        padding-right: 0;
    }
    `}
`;
export const SplitMain = styled.div`
    flex: ${({ $ratio }) => ($ratio != null ? `${1 - $ratio} 1 0` : '1 1 0')};
    min-width: 0;
    padding-left: ${({ $divider }) => ($divider ? '20px' : 0)};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    ${({ $responsive }) =>
        $responsive &&
        `
    @media (max-width: 768px) {
        order: 1;
        padding-left: 0;
    }
    `}
`;
/** Details tab: top block (title, badges, progress, assignee). On small screen stays at top (order 0). */
export const DetailsTabTopBlock = styled.div`
    flex-shrink: 0;
    @media (max-width: 768px) {
        order: 0;
    }
`;
/** Details tab: attachment block (attach URL + files). On small screen shows below description (order 2). */
export const DetailsTabAttachmentBlock = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    @media (max-width: 768px) {
        order: 2;
    }
`;

/** External links in a row: each item only as wide as its text (capped); long URLs wrap inside the cap. */
export const ExternalLinkWrapRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px 10px;
    align-items: flex-start;
    width: 100%;
`;

export const ExternalLinkWrapCell = styled.div`
    flex: 0 1 auto;
    align-self: flex-start;
    min-width: 0;
    max-width: min(100%, 260px);
    width: max-content;
    box-sizing: border-box;
    padding: 4px;
    border-radius: 40px;
    border: 1px solid
        ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'transparent' : 'rgba(0,0,0,0.03)'};
`;

/** Client mailto in project details: primary color, underline on hover (no black). */
export const DetailClientMailtoLink = styled.a`
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    font-family: var(--font-family-geist);
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    color: ${({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#7c3aed'};
    text-decoration: none;
    cursor: pointer;
    transition:
        color 0.2s ease,
        opacity 0.2s ease;
    &:hover {
        color: ${({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#7c3aed'};
        text-decoration: underline;
        text-underline-offset: 3px;
        opacity: 0.92;
    }
    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#7c3aed'};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

/** Horizontal row of attachment-style cards (e.g. file previews + add card). */
export const AttachmentCardsRow = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 8px;
`;
/** Single attachment preview card: rounded, shadow, fixed size. */
export const AttachmentCard = styled.div`
    width: 120px;
    height: 120px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : '#e8e8e8')};
    background: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'transparent' : '#fff')};
    box-shadow: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'none' : '0 1px 4px rgba(0,0,0,0.08)'};
    display: flex;
    align-items: center;
    justify-content: center;
    img, video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;
/** Add-attachment card: grey background, plus icon. */
export const AttachmentAddCard = styled.button`
    width: 120px;
    height: 120px;
    border-radius: 12px;
    flex-shrink: 0;
    border: 1px dashed ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.2)' : '#d9d9d9')};
    background: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'transparent' : '#fafafa')};
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: background 0.2s, border-color 0.2s;
    &:hover {
        background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? theme.colors.background.tertiary : '#f0f0f0'};
        border-color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#595959' : '#bfbfbf')};
    }
`;

/** Project details tab: large project name. */
export const DetailsProjectTitle = styled.h1`
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 12px 0;
    line-height: 1.3;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.88)')};
`;
/** Thin divider under project name. */
export const DetailsDivider = styled.hr`
    border: none;
    height: 1px;
    background: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
    margin: 0 0 16px 0;
`;
/** Row of badges (status, priority, created at). */
export const DetailsBadgeRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
`;
/** Small section label in details left column. */
export const DetailsSectionLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)')};
    margin-bottom: 8px;
`;
/** Scrollable grid for attachment files: auto-fill 120px columns, 10px gap. */
export const DetailsAttachmentGridScroll = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, 120px);
    gap: 10px;
    overflow-y: auto;
    min-height: 0;
    flex: 1;
    padding-right: 4px;
    align-content: start;
`;

/** Project detail screen: large title (reference layout). */
export const DetailScreenTitle = styled.h1`
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 28px 0;
    line-height: 1.25;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000')};
    font-family: var(--font-family-geist);
`;

/** Label column: medium grey, colon in copy from JSX. */
export const DetailMetaKeyLabel = styled.span`
    flex: 0 0 168px;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#888888')};
    font-family: var(--font-family-geist);
    line-height: 1.5;
    padding-top: 2px;
`;

export const DetailMetaRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 24px;
`;

export const DetailMetaValueCol = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000')};
    font-family: var(--font-family-geist);
`;

export const DetailProgressTrack = styled.div`
    width: 110px;
    flex: 0 0 110px;
    height: 8px;
    border-radius: 999px;
    background: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#3a3a3a' : '#e8e8e8')};
    overflow: hidden;
`;

export const DetailProgressFillTeal = styled.div`
    height: 100%;
    border-radius: 999px;
    width: ${({ $percent }) => Math.min(100, Math.max(0, $percent))}%;
    background: #2a9d90;
    transition: width 0.2s;
`;

export const DetailProgressPercentText = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000')};
    font-family: var(--font-family-geist);
    flex-shrink: 0;
`;

/** Sub-tabs: purple underline on active (#7E57C2). */
export const SubTabsUnderlineRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 32px;
    border-bottom: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
    margin-bottom: 20px;
    flex-shrink: 0;
`;

/** Same visual as SubTabsUnderlineRow but sticks to top; use inner row for tab buttons + actions on the right. */
export const StickySubTabsUnderlineRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    padding-left: 16px;
    padding-right: 16px;
    border-bottom: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 10;
    margin-bottom: 16px;
    padding-top: 0;
    margin-top: 0;
    /* Match Inside.jsx main Container: dark mode uses background.secondary, not default (#141414). */
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? theme.colors.background.secondary : '#ffffff'};
    box-shadow: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414'
            ? '0 6px 12px -8px rgba(0,0,0,0.45)'
            : '0 4px 12px -6px rgba(0,0,0,0.12)'};
`;

/** Left group: Description | Attachments tab buttons (gap matches SubTabsUnderlineRow). */
export const SubTabsUnderlineTabsGroup = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 32px;
    flex: 1;
    min-width: 0;
`;

export const SubTabsUnderlineButton = styled.button`
    padding: 12px 0;
    margin-bottom: -1px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 14px;
    font-family: var(--font-family-geist);
    color: ${({ $active, theme }) => {
        if ($active) return theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000';
        return theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#888888';
    }};
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    border-bottom: 2px solid ${({ $active }) => ($active ? '#7e57c2' : 'transparent')};
    transition: color 0.15s, border-color 0.15s;
    &:hover {
        color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000')};
    }
`;

/** Person chip: light grey bg, thin border (team / PM). Matches Invite height (4px padding, 24px avatar, 13px label). */
export const DetailPersonChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    border-radius: 40px;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000')};
    background: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#2c2c2c' : '#f5f5f5')};
    border: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#434343' : '#d9d9d9')};
    font-family: var(--font-family-geist);
    box-sizing: border-box;
`;

export const DetailPersonChipAvatar = styled.span`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    color: #fff;
    background: ${({ $bg }) => $bg || '#8b5cf6'};
    flex-shrink: 0;
`;

export const DetailInviteOutlineButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 4px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#434343' : '#d9d9d9')};
    background: transparent;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    box-sizing: border-box;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#fff' : '#000000')};
    cursor: pointer;
    font-family: var(--font-family-geist);
    .anticon {
        font-size: 13px;
    }
    &:hover {
        border-color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#595959' : '#bfbfbf')};
    }
`;

/** Generic nav link button: transparent, primary color, inline-flex. Use for Back / link-style actions in headers. */
export const NavLinkButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: var(--ant-color-primary);
    font-family: inherit;
    &:hover {
        opacity: 0.85;
    }
`;

/** Wrapper for icon-only button: default = primary border + primary icon; hover = primary purple fill, no border, white icon. Reusable for send/post actions. */
export const OutlineIconButtonWrap = styled.div`
    display: inline-flex;
    .ant-btn {
        border: 1px solid ${({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6'} !important;
        color: ${({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6'} !important;
        background: transparent !important;
    }
    .ant-btn .anticon {
        color: inherit;
    }
    .ant-btn:hover:not(:disabled):not(.ant-btn-loading) {
        background: ${({ theme }) =>
        theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6'} !important;
        border-color: ${({ theme }) =>
        theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6'} !important;
        color: #fff !important;
    }
`;

/** Optional sticky header inside body container (e.g. Back button, title). Use when needed; not shown by default. Sticks on scroll. */
export const BodyContainerHeader = styled.div`
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 10;
    min-height: ${({ $minHeight }) => $minHeight ?? '44px'};
    padding: ${({ $padding }) => $padding ?? '8px 0'};
    display: flex;
    align-items: center;
    gap: 12px;
    background: ${({ theme, $sprintsWhiteSurface }) => {
        if ($sprintsWhiteSurface && theme.colors.background.default !== '#141414') return '#ffffff';
        return theme.colors.background.default === '#141414'
            ? theme.colors.background.secondary || '#1f1f1f'
            : '#FAFAFA';
    }};
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#303030' : '#ECECF0')};
    margin-bottom: 0;
`;
/** Active tab title as page header in body container */
export const BodyContainerTitle = styled.h1`
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    margin: 0;
    font-family: var(--font-family-geist);
`;

export const FieldLabelSmall = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    margin-bottom: 6px;
`;

/** Bold, large heading for panel/card (e.g. bug card main line). Generic use. */
export const PanelCardHeading = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    word-break: break-word;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    font-family: var(--font-family-geist);
`;

/** Section label with underline for description/steps in panel cards. Generic use. */
export const PanelCardSectionLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#e8e8e8')};
    font-family: var(--font-family-geist);
`;

/** Expandable body for accordion cards; use $expanded and max-height for open/close. */
export const PanelCardExpandable = styled.div`
    overflow: hidden;
    max-height: ${({ $expanded }) => ($expanded ? '280px' : '0')};
    transition: max-height 0.25s ease-out;
    & > div {
        padding-top: 4px;
    }
`;

/** Section label for detail/panel content (e.g. "Task context", "Reported bugs"). Generic use. */
export const PanelSectionLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
    margin-bottom: 10px;
    font-family: var(--font-family-geist);
`;

/** Card with optional left accent border. Pass $accent as CSS color (e.g. #ff4d4f). Generic use. */
export const AccentBorderCard = styled(ContentCard)`
    border-left: 3px solid ${({ $accent }) => $accent || 'transparent'};
`;

/** Subtle divider line for use inside panels/cards. Theme-aware. */
export const PanelDivider = styled.div`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
`;

export const FieldLabelSmallWithMargin = styled(FieldLabelSmall)`
    margin-top: 12px;
`;

export const MutedText = styled.div`
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
    margin-bottom: ${({ $marginBottom }) => $marginBottom ?? '0'};
    font-family: var(--font-family-geist);
`;

export const ListCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    overflow: hidden;
`;

/** Dashboard list container: square (no border-radius), for notifications/activity. */
export const DashboardListCard = styled(ListCard)`
    border-radius: 0;
`;

export const ListItem = styled.button`
    width: 100%;
    display: block;
    text-align: left;
    padding: 16px 20px;
    border: none;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    background: ${({ $read, theme }) =>
        $read
            ? 'transparent'
            : theme.colors.background.default === '#141414'
                ? 'rgba(139, 92, 246, 0.08)'
                : 'rgba(139, 92, 246, 0.06)'};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#333')};
    font-size: 14px;
    cursor: pointer;
    transition: background 0.15s;
    &:last-child {
        border-bottom: none;
    }
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#fafafa')};
    }
`;

/** List card whose list items use the given horizontal padding (e.g. to align with page header). */
export const ListCardWithItemPadding = styled(ListCard)`
    & ${ListItem} {
        padding-left: ${({ $itemPaddingX }) => $itemPaddingX || '28px'};
        padding-right: ${({ $itemPaddingX }) => $itemPaddingX || '28px'};
    }
`;

export const ListItemTitle = styled.div`
    font-weight: 600;
    margin-bottom: 4px;
`;

export const ListItemBody = styled.div`
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#666')};
    margin-bottom: 6px;
`;

export const ListItemMeta = styled.div`
    font-size: 12px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#888')};
`;

/** Small dot for unread/status indicator (e.g. notifications, list items). */
export const UnreadDot = styled.span`
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.background.primary || '#8b5cf6'};
    margin-top: 6px;
`;

/** Flex row for list item content with optional leading indicator (e.g. UnreadDot). */
export const ListItemContentRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    min-width: 0;
`;

/** Segment control: row of toggle buttons (e.g. Overview, Unread, Read). */
export const SegmentTabs = styled.div`
    display: inline-flex;
    border-radius: 10px;
    padding: 4px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#252525' : '#f0f0f0')};
    gap: 2px;
`;
export const SegmentTab = styled.button`
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition:
        background 0.2s,
        color 0.2s;
    background: transparent;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
    &:hover {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
    }
    ${({ $active, theme }) =>
        $active
            ? `
        background: ${theme.colors.background.default === '#141414' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.12)'};
        color: ${theme.colors.background.default === '#141414' ? '#fff' : '#2a1b50'};
    `
            : ''}
`;

/** Stats strip for counts (e.g. Total, Unread, Read). */
export const StatsStrip = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
`;
export const StatItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
`;
export const StatValue = styled.span`
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
`;

/** Simple centered message (errors, dashboards, tables). */
export const EmptyState = styled.div`
    padding: 48px 24px;
    text-align: center;
    font-size: 15px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#888')};
`;

/** Centered block: optional icon, title, description — tabs, panels, lists. */
export const EmptyStateBlock = styled.div`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 48px 24px;
        text-align: center;
        min-height: 200px;
        backgroundColor: "${({ theme }) => theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.02)' : '#f5f5f5'}",
    `;
export const EmptyStateIcon = styled.span`
    font-size: 48px;
    line-height: 1;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.22)' : '#d9d9d9')};
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;
export const EmptyStateTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#595959')};
    margin-bottom: 4px;
`;
export const EmptyStateDescription = styled.div`
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
`;

/** Fills flex parent: centered message (e.g. chat pane). */
export const EmptyStateFill = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
    text-align: center;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#999')};
    font-size: 15px;
`;

/** Dashed inset for empty column slots (board columns, small regions). */
export const EmptyStateInset = styled.div`
    padding: 16px;
    text-align: center;
    border-radius: 8px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.02)' : '#f5f5f5'};
    border: 1px dashed ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#1a1a1a')};
`;

/** Generic attachment thumbnail box for any module (e.g. milestones, chat). */
export const AttachmentThumbnailBox = styled.div`
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--ant-color-border);
    background: var(--ant-color-fill-quaternary, #f5f5f5);
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

export const AttachmentThumbnailImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

export const AttachmentThumbnailFileWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 4px 8px;
    text-align: center;
`;

export const AttachmentThumbnailFileExt = styled.div`
    font-size: 10px;
    font-weight: 800;
    color: var(--ant-color-text-tertiary, #8c8c8c);
    text-transform: uppercase;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const AttachmentRemoveBtn = styled.button`
    position: absolute;
    top: 2px;
    right: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: none;
    background: rgba(0,0,0,0.45);
    color: #fff;
    cursor: pointer;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    backdrop-filter: blur(4px);
`;

export const DetailWrap = styled.div`
    padding: 0 4px;
    min-width: 0;
    overflow-x: hidden;
`;
export const DetailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;
export const CodeLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    font-family: var(--font-family-geist);
`;
export const DetailTitle = styled.h2`
    font-size: 22px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    margin: 0 0 20px 0;
    font-family: var(--font-family-geist);
    line-height: 1.3;
`;
export const DetailTitleSmall = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    margin: 0 0 12px 0;
    font-family: var(--font-family-geist);
    line-height: 1.35;
`;
export const MetaRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
`;
export const MetaRowInline = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
`;
export const MetaLabel = styled.span`
    flex: 0 0 140px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
    font-family: var(--font-family-geist);
`;
export const MetaValue = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-family: var(--font-family-geist);
    overflow-wrap: break-word;
    word-break: break-word;
`;
export const MetaValueMuted = styled(MetaValue)`
    opacity: 0.7;
`;
export const MetaValueInherit = styled(MetaValue)`
    color: inherit;
`;
export const ProgressWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
`;
export const ProgressBar = styled.div`
    flex: 1;
    height: 8px;
    border-radius: 4px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    overflow: hidden;
`;
export const ProgressFill = styled.div`
    height: 100%;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.primary || '#7543DC'};
    width: ${({ $percent }) => Math.min(100, Math.max(0, $percent))}%;
    transition: width 0.2s;
`;
export const ProgressText = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-family: var(--font-family-geist);
    flex-shrink: 0;
`;
export const PillsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;
export const Pill = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 8px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f5f5f5')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#e8e8e8')};
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-family: var(--font-family-geist);
`;
export const CategoryPill = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#666')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f0f0f0')};
    padding: 4px 10px;
    border-radius: 6px;
`;
export const Avatar = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
`;
export const TabsStrip = styled.div`
    display: flex;
    gap: 0;
    flex-shrink: 0;
    overflow-x: auto;
    overflow-y: hidden;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    margin: 24px 0 16px 0;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
        height: 4px;
    }
`;
export const TabButton = styled.button`
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 500;
    font-family: var(--font-family-geist);
    flex-shrink: 0;
    white-space: nowrap;
    color: ${({ $active, theme }) =>
        $active
            ? theme.colors.primary || '#7543DC'
            : theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.65)'
                : '#666'};
    background: none;
    border: none;
    border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary || '#7543DC' : 'transparent')};
    margin-bottom: -1px;
    cursor: pointer;
    transition:
        color 0.2s,
        border-color 0.2s;
    &:hover {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    }
`;
export const TabContent = styled.div`
    margin-top: 0;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    width: 100%;
    background: ${({ theme, $sprintsWhiteSurface }) =>
        $sprintsWhiteSurface && theme.colors.background.default !== '#141414' ? '#ffffff' : 'transparent'};
    /* Kanban project tabs: fill PageBodyScroll; inner Kanban columns scroll (see layout $lockHeight). */
`;

/** Modern list card row with optional left accent (e.g. for milestones, tasks) */
export const ContentListCard = styled.div`
    display: flex;
    align-items: stretch;
    min-height: 0;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#383838' : '#e8e8e8')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.03)' : '#fff')};
    overflow: hidden;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;
    &:hover {
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
        box-shadow: 0 1px 4px
            ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)')};
    }
    & + & {
        margin-top: 10px;
    }
`;
export const ContentListCardAccent = styled.div`
    width: 4px;
    flex-shrink: 0;
    background: ${({ $color, theme }) => $color || theme.colors.primary || '#7543DC'};
`;
export const ContentListCardBody = styled.div`
    flex: 1;
    min-width: 0;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
`;
export const ContentListCardMain = styled.div`
    min-width: 0;
`;
export const ContentListCardTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    font-family: var(--font-family-geist);
    line-height: 1.4;
    margin-bottom: 4px;
`;
export const ContentListCardMeta = styled.div`
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#6b7280')};
    font-family: var(--font-family-geist);
`;

export const CardListItem = styled.div`
    padding: 10px 12px;
    border-radius: 8px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#fff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#f0f0f0')};
    margin-bottom: 8px;
    font-size: 13px;
    font-family: var(--font-family-geist);
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#333')};
    &:last-child {
        margin-bottom: 0;
    }
`;

/** Scrollable container for hierarchy tree (e.g. Milestone → Sprint → Task → Bugs). */
export const HierarchyTreeScroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 8px;
`;

/** One level of hierarchy (collapsible section). Use with HierarchySectionHeader + HierarchySectionBody. */
export const HierarchySection = styled.div`
    margin-bottom: 8px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#383838' : '#e8e8e8')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.02)' : '#fff')};
    overflow: hidden;
`;

/** Clickable header row for hierarchy section; use $level (0–3) for indent. */
export const HierarchySectionHeader = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: ${({ $level }) => {
        const base = 14;
        const indent = ($level ?? 0) * 16;
        return `${base}px ${16 + indent}px`;
    }};
    border: none;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.03)' : '#fafafa')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    font-size: ${({ $level }) => ($level === 0 ? 15 : $level === 1 ? 14 : 13)}px;
    font-weight: ${({ $level }) => ($level === 0 ? 600 : $level === 1 ? 600 : 500)};
    font-family: var(--font-family-geist);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : '#f0f0f0')};
    }
`;

/** Body of hierarchy section; optional left border for nesting. */
export const HierarchySectionBody = styled.div`
    border-top: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
    padding: ${({ $nested }) => ($nested ? '8px 0 8px 8px' : '12px')};
`;

/** Single row in hierarchy (e.g. bug row); clickable. */
export const HierarchyRow = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#333')};
    font-size: 13px;
    font-family: var(--font-family-geist);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : '#f5f5f5')};
    }
`;

export const UpgradeBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 420px;
    padding: 48px 24px;
    text-align: center;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1a1a1a' : '#fafafa')};
    border: 1px dashed ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
    border-radius: 16px;
    .upgrade-icon {
        font-size: 56px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#faad14' : '#d48806')};
        margin-bottom: 20px;
    }
    .upgrade-title {
        font-size: 20px;
        font-weight: 600;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
        margin: 0 0 8px 0;
        font-family: var(--font-family-geist);
    }
    .upgrade-message {
        font-size: 14px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#666')};
        margin: 0 0 28px 0;
        max-width: 400px;
    }
`;

export const PlanCardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 24px;
    width: 100%;
    max-width: 900px;
    margin: 24px auto;
    padding: 24px 0;
`;

export const PlanCard = styled.button`
    min-width: 0;
    border-radius: 12px;
    overflow: hidden;
    border: 3px solid ${({ $selected }) => ($selected ? '#2563eb' : 'transparent')};
    background: #fff;
    text-align: center;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transition:
        transform 0.2s,
        box-shadow 0.2s;
    padding: 0;
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    }
`;

export const PlanCardHeader = styled.div`
    padding: 16px 12px;
    background: ${({ $color }) => $color || '#64748b'};
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    border-radius: 12px 12px 0 0;
`;

export const PlanCardPrice = styled.div`
    padding: 20px 16px 8px;
    font-size: 1.5rem;
    font-weight: 800;
    color: ${({ $color }) => $color || '#1e293b'};
    line-height: 1.2;
`;

export const PlanCardPriceUnit = styled.span`
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
`;

export const PlanCardFeatures = styled.div`
    padding: 12px 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const PlanCardFeature = styled.div`
    padding: 10px 12px;
    background: #f1f5f9;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #475569;
    text-align: left;
`;

export const ErrorBlock = styled.div`
    padding: 12px 16px;
    margin-bottom: 16px;
    border-radius: 8px;
    background: #fff2f0;
    color: #cf1322;
    font-size: 14px;
`;

export const DetailPanelLoading = styled.div`
    display: flex;
    justify-content: center;
    padding: 48px;
`;

export const DetailPanelContentWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
`;

export const DetailPanelScroll = styled.div`
    flex: 1;
    min-height: 0;
`;

export const DetailPanelFooter = styled.div`
    flex-shrink: 0;
    display: flex;
    gap: 12px;
    padding-top: 24px;
    margin-top: auto;
    position: sticky;
    bottom: 0;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.secondary : '#ffffff'};
    border-top: 1px solid
        ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#f0f0f0')};
`;

export const DetailPanelFooterBtn = styled.div`
    flex: 1;
    min-width: 0;
`;

export const GridTwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
    margin-top: 16px;
`;

export const GridTwoColNoMargin = styled(GridTwoCol)`
    margin-top: 0;
`;

export const HighlightCard = styled.div`
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(139, 92, 246, 0.12)' : '#f9f0ff'};
    border: 1px solid
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(139, 92, 246, 0.35)' : '#d3adf7')};
    border-radius: 12px;
    padding: 16px 20px;
`;

export const ActionsWrap = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

export const ButtonWrap = styled.div`
    margin-top: 8px;
`;

export const DetailValueError = styled(DetailValue)`
    color: var(--ant-color-error);
`;
export const DetailValueInherit = styled(DetailValue)`
    color: inherit;
`;

/* Chat page - fills parent so footer touches bottom; header and footer stick, only message list scrolls */
export const ChatFullHeightWrap = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    min-height: 0;
    overflow: hidden;
`;

export const ChatLayout = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    overflow: hidden;
    position: relative;
`;

export const ChatListPanel = styled.div`
    width: 320px;
    min-width: 280px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    @media (max-width: 768px) {
        width: 100%;
        min-height: 200px;
        border-right: none;
        border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    }
`;

export const ChatListHeader = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    font-weight: 600;
    font-size: 16px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
`;

export const ChatListScroll = styled.div`
    flex: 1;
    overflow-y: auto;
`;

export const ChatListItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    border: none;
    border-bottom: 1px solid
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : '#f5f5f5')};
    background: ${({ $active, theme }) =>
        $active
            ? theme.colors.background.default === '#141414'
                ? 'rgba(139, 92, 246, 0.15)'
                : 'rgba(139, 92, 246, 0.08)'
            : 'transparent'};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#333')};
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#fafafa')};
    }
`;

export const ChatListAvatar = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9375rem;
    flex-shrink: 0;
`;

export const ChatListItemInfo = styled.div`
    min-width: 0;
    flex: 1;
`;

/** Two-line clamp with ellipsis; use for table cells or cards. */
export const TextLineClampTwo = styled.div`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
`;

export const ChatListItemName = styled.div`
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const ChatListItemMeta = styled.div`
    font-size: 12px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#888')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
`;

export const ChatMessagePanel = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    background: transparent;
`;

/* Only this area scrolls; empty state keeps same body height */
export const ChatMessageList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    user-select: text;
    -webkit-user-select: text;
`;

/* Wrapper for overlay loader / top bounce – does not take line space */
export const ChatMessageListWrap = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    user-select: text;
    -webkit-user-select: text;
`;

export const LoadMoreOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px;
    pointer-events: none;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#555')};
    font-size: 13px;
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414'
            ? 'linear-gradient(to bottom, rgba(37,37,37,0.92) 0%, transparent 100%)'
            : 'linear-gradient(to bottom, rgba(250,250,250,0.92) 0%, transparent 100%)'};
`;

const chatTopBounce = keyframes`
    0%, 100% { transform: translateX(-50%) translateY(0); }
    30% { transform: translateX(-50%) translateY(-6px); }
    50% { transform: translateX(-50%) translateY(2px); }
    70% { transform: translateX(-50%) translateY(-2px); }
`;

/** Wrapper for top bounce badge – position + bounce animation only */
export const TopBounceBadgeWrap = styled.div`
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    pointer-events: none;
    animation: ${chatTopBounce} 0.5s ease-out;
`;

/** Wrapper for "new messages" badge – fixed near bottom, clickable */
export const BottomNewMessageBadgeWrap = styled.div`
    position: absolute;
    left: 50%;
    bottom: 18px;
    transform: translateX(-50%);
    z-index: 3;
    pointer-events: auto;
`;

/** Floating down-arrow jump-to-latest button */
export const BottomJumpToLatestWrap = styled.div`
    position: absolute;
    right: 18px;
    bottom: 18px;
    z-index: 3;
    pointer-events: auto;
`;

export const ChatMessageRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    max-width: 100%;
`;

export const ChatMessageAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    flex-shrink: 0;
`;

export const ChatMessageCol = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const ChatMessageMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: nowrap;
    min-width: 0;
`;

export const ChatMessageSender = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

export const ChatMessageTime = styled.span`
    font-size: 11px;
    color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)'};
    line-height: 1;
    flex-shrink: 0;
    white-space: nowrap;
`;

export const ChatMessageBody = styled.div`
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#333')};
    padding: 10px 14px;
    border-radius: 12px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : 'rgba(0,0,0,0.04)')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : 'rgba(0,0,0,0.06)')};
    width: fit-content;
    max-width: 100%;
`;

/** Wraps discussion list: orange avatar + left accent on bubble (project detail reference). */
export const ThreadFeedAccentWrap = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    ${ChatMessageAvatar} {
        background: #ef6c00 !important;
        background-image: none !important;
    }
    ${ChatMessageBody} {
        background: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#2c2c2c' : '#f5f5f5')} !important;
        border: none !important;
        border-left: 3px solid #ef6c00 !important;
        border-radius: 8px !important;
        color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#333333')} !important;
    }
    ${ChatMessageTime} {
        font-size: 12px !important;
        color: #888888 !important;
    }
`;

/* Typing indicator: bubble with 3-dot wave animation */
const typingWave = keyframes`
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-4px); }
`;

export const TypingBubbleWrap = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    margin-top: 4px;
`;

export const TypingBubble = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 16px;
    border-radius: 12px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : 'rgba(0,0,0,0.04)')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : 'rgba(0,0,0,0.06)')};
    width: fit-content;

    .typing-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#888'};
        animation: ${typingWave} 1.4s ease-in-out infinite;
    }
    .typing-dot:nth-child(1) {
        animation-delay: 0s;
    }
    .typing-dot:nth-child(2) {
        animation-delay: 0.2s;
    }
    .typing-dot:nth-child(3) {
        animation-delay: 0.4s;
    }
`;

export const TypingBubbleLabel = styled.span`
    font-size: 11px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#888')};
    margin-left: 4px;
`;

/* Receiver: light + clear border and tail. Sender: purple. */
const receiverBgLight = '#e0e7ff';
const receiverBgDark = '#64748b';
const receiverBorderLight = '#a5b4fc';
const receiverBorderDark = '#94a3b8';

export const ChatBubbleWrap = styled.div`
    position: relative;
    width: fit-content;
    max-width: 100%;
    align-self: ${({ $isOwn }) => ($isOwn ? 'flex-end' : 'flex-start')};
`;
export const ChatBubble = styled.div`
    position: relative;
    font-size: 14px;
    line-height: 1.5;
    padding: 10px 14px;
    border-radius: 16px;
    word-break: break-word;
    overflow-wrap: break-word;
    color: ${({ $isOwn, theme }) =>
        $isOwn ? '#fff' : theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.95)' : '#1e293b'};
    background: ${({ $isOwn, theme }) =>
        $isOwn
            ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
            : theme.colors.background.default === '#141414'
                ? receiverBgDark
                : receiverBgLight};
    border: ${({ $isOwn, theme }) =>
        $isOwn
            ? 'none'
            : theme.colors.background.default === '#141414'
                ? `2px solid ${receiverBorderDark}`
                : `2px solid ${receiverBorderLight}`};
    box-shadow: ${({ $isOwn, theme }) =>
        $isOwn
            ? '0 1px 2px rgba(0,0,0,0.08)'
            : theme.colors.background.default === '#141414'
                ? '0 1px 3px rgba(0,0,0,0.25)'
                : '0 1px 3px rgba(99, 102, 241, 0.12)'};
    &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border: 8px solid transparent;
        bottom: 0;
        ${({ $isOwn, theme }) => {
        const dark = theme.colors.background.default === '#141414';
        const borderColor = dark ? receiverBorderDark : receiverBorderLight;
        return $isOwn
            ? 'right: -5px; border-left-color: #6366f1; border-bottom-color: #6366f1;'
            : `left: -5px; border-right-color: ${borderColor}; border-bottom-color: ${borderColor};`;
    }}
    }
    &::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border: 6px solid transparent;
        bottom: 2px;
        ${({ $isOwn, theme }) => {
        const dark = theme.colors.background.default === '#141414';
        const otherBg = dark ? receiverBgDark : receiverBgLight;
        return $isOwn
            ? 'right: -3px; border-left-color: #6366f1; border-bottom-color: #6366f1;'
            : `left: -3px; border-right-color: ${otherBg}; border-bottom-color: ${otherBg};`;
    }}
    }
`;

/** Two-column layout: sidebar list + main detail (e.g. Projects list + lead detail). */
export const PanelWithSidebar = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
    overflow: hidden;
`;

export const SidebarListPanel = styled.div`
    width: 300px;
    min-width: 260px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
`;

export const DetailContentPanel = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#252525' : '#fafafa')};
`;

/** Global vertical timeline – center line, cards alternate left/right. Reusable by any module (e.g. milestones, deliveries). */
export const TimelineWrap = styled.div`
    position: relative;
    padding: 24px 0;
`;
export const TimelineLine = styled.div`
    position: absolute;
    left: 50%;
    margin-left: -1px;
    top: 24px;
    bottom: 24px;
    width: 2px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#555' : '#d0d0d0')};
    border-radius: 1px;
    z-index: 0;
`;
export const TimelineItem = styled.div`
    position: relative;
    width: 100%;
    padding-bottom: 32px;
    z-index: 1;
    &:last-child {
        padding-bottom: 0;
    }
`;
export const TimelineItemContent = styled.div`
    position: relative;
    box-sizing: border-box;
    width: 50%;
    ${({ $side }) =>
        $side === 'left'
            ? `
        padding-right: 14px;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    `
            : `
        margin-left: 50%;
        padding-left: 14px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    `}
`;
export const TimelineIconWrap = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 4px;
    z-index: 2;
`;
export const TimelineIcon = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ $color }) => $color || 'var(--ant-color-primary)'};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: 0 0 0 4px
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.9)')};
`;
export const TimelineDate = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    margin-bottom: 6px;
    text-align: ${({ $side }) => ($side === 'right' ? 'left' : 'right')};
`;
export const TimelineCard = styled.div`
    max-width: ${({ $large }) => ($large ? '480px' : '320px')};
    width: ${({ $large }) => ($large ? '100%' : 'max-content')};
    padding: ${({ $large }) => ($large ? '18px 20px' : '14px 18px')};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#e8e8e8')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.05)' : '#fff')};
    box-shadow: 0 1px 4px
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.06)')};
    transition: all 0.2s ease-in-out;
    cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

    ${({ $interactive, theme }) =>
        $interactive &&
        css`
            &:hover {
                box-shadow: 0 4px 12px ${theme.colors.background.default === '#141414' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.12)'};
                border-color: ${theme.colors.background.default === '#141414' ? '#595959' : '#d9d9d9'};
            }
        `}
`;
export const TimelineCardDetail = styled.div`
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#eee')};
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.75)' : '#555')};
    line-height: 1.5;
`;
export const TimelineCardTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    margin-bottom: 6px;
    font-family: var(--font-family-geist);
`;
export const TimelineCardMeta = styled.div`
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#6b7280')};
    display: flex;
    align-items: center;
    gap: 6px;
`;


/** Thin progress track with pill-shaped ends (sprint detail row). */
export const ProgressBarThinRound = styled(ProgressBar)`
    height: 6px;
    border-radius: 999px;
`;

export const ProgressFillViolet = styled(ProgressFill)`
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? '#B388FF' : '#7C4DFF'} !important;
`;

/** Sprint detail row: fixed 110px bar + percent (no flex-grow on track). */
export const BoardDetailProgressWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: nowrap;
    width: auto;
`;

export const BoardDetailProgressBarTrack = styled(ProgressBarThinRound)`
    flex: 0 0 110px;
    width: 110px;
    min-width: 110px;
    max-width: 110px;
`;

export const BoardDetailProgressPercent = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#000')};
    font-family: var(--font-family-geist);
    flex-shrink: 0;
`;

/** Sprint Kanban column fill + stroke (Todo / Mark As Completed / Submitted — light mode hex per spec). */
const kanbanColumnPalette = (variant, theme) => {
    const isDark = theme?.colors?.background?.default === '#141414';
    switch (variant) {
        case 'completed':
            /* Mark completed: same hue as #E9FDEE / #152a1a at 30% opacity */
            return isDark
                ? { fill: 'rgba(21, 42, 26, 0.3)', stroke: '#2d6a40' }
                : { fill: 'rgba(233, 253, 238, 0.3)', stroke: '#ABFBC0' };
        case 'resolved':
            /* Resolved column: green/teal tint */
            return isDark
                ? { fill: 'rgba(16, 185, 129, 0.2)', stroke: '#10b981' }
                : { fill: '#ecfdf5', stroke: '#a7f3d0' };
        case 'submitted':
            /* Submitted column: purple tint at 40% opacity (#E7DEF9 hue / dark purple base) */
            return isDark
                ? { fill: 'rgba(91, 33, 182, 0.4)', stroke: '#7c3aed' }
                : { fill: 'rgba(231, 222, 249, 0.4)', stroke: '#DED1F9' };
        case 'bugs':
            /* QA: Bugs reported column — light peach fill */
            return isDark
                ? { fill: 'rgba(234, 88, 12, 0.2)', stroke: '#ea580c' }
                : { fill: '#fdf1ee', stroke: '#fdba74' };
        case 'todo':
        default:
            return isDark
                ? { fill: '#252525', stroke: '#404040' }
                : { fill: '#F7F7F8', stroke: '#E0E0E0' };
    }
};

/** Column header title + count total: same primary text color (matches KanbanColumnTitle). */
const kanbanColumnHeaderTextColor = (theme) =>
    theme.colors.background.default === '#141414' ? '#f3f4f6' : '#111827';

/**
 * Sprint Kanban outer shell: guarantees usable height even when parent (e.g. ant Collapse) does not
 * pass flex height down. Columns scroll inside; this block fills viewport minus chrome.
 */
export const SprintKanbanFill = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    width: 100%;
    overflow: hidden;
`;

/** Kanban row: desktop = 3 columns + horizontal scroll; narrow = vertical stack (one column after another). */
export const KanbanThreeColumnRow = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 16px;
    align-items: stretch;
    flex: 1 1 0%;
    min-height: 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    box-sizing: border-box;

    @media (max-width: 1024px) {
        flex-direction: column;
        flex-wrap: nowrap;
        align-items: stretch;
        align-content: flex-start;
        overflow-x: hidden;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        padding: 0;
        gap: 20px;
    }
`;

/** Kanban column — default: equal flex on desktop; pass `$fixedDesktop` for fixed 340px columns (e.g. Blockers). */
export const KanbanColumnPanel = styled.div`
    ${({ $fixedDesktop }) =>
        $fixedDesktop
            ? css`
                  flex: 0 0 ${KANBAN_DESKTOP_COLUMN_MIN_PX}px !important;
                  width: ${KANBAN_DESKTOP_COLUMN_MIN_PX}px !important;
                  min-width: ${KANBAN_DESKTOP_COLUMN_MIN_PX}px !important;
                  max-width: ${KANBAN_DESKTOP_COLUMN_MIN_PX}px !important;
              `
            : css`
                  flex: 1 1 0%;
                  width: 0;
                  min-width: ${KANBAN_DESKTOP_COLUMN_MIN_PX}px;
                  max-width: none;
              `}
    display: flex;
    flex-direction: column;
    gap: 0;
    min-height: 0;
    border-radius: 0;
    border: none;
    background: transparent;
    /* Hidden so column body can be the scrollport (flex child with min-height:0) */
    overflow: hidden;
    padding: 0;

    @media (max-width: 1024px) {
        flex: 0 0 auto;
        align-self: stretch;
        width: 100%;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
    }
`;

const kanbanDropIconBobKeyframes = keyframes`
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
`;

/** Sprint Kanban — drop hint for TODO / Submitted columns (primary) while dragging */
export const KanbanColumnDropHint = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    padding: 8px 10px;
    margin: 0;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors?.primary || '#7c3aed'};
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(124, 58, 237, 0.12)' : 'rgba(124, 58, 237, 0.08)'};
    border: 1px dashed ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(167, 139, 250, 0.45)' : 'rgba(124, 58, 237, 0.35)'};
    font-family: var(--font-family-geist), system-ui, sans-serif;
`;

/** Shown while dragging over the completed / bugs column — drop zone hint */
export const KanbanCompletedDropHint = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    padding: 8px 10px;
    margin: 0;
    border-radius: 8px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#4ade80' : '#15803d')};
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.14)'};
    border: 1px dashed ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(74, 222, 128, 0.45)' : 'rgba(21, 128, 61, 0.35)'};
    font-family: var(--font-family-geist), system-ui, sans-serif;
`;

/** Full column body placeholder while dragging from another column — grey dashed frame, icon + text (not clipped). */
export const KanbanDragDropPlaceholder = styled.div`
    flex: 1 1 0%;
    min-height: 120px;
    width: 100%;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 20px 14px;
    margin: 0;
    box-sizing: border-box;
    border: 2px dashed
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.22)' : '#d1d5db')};
    border-radius: 12px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(249, 250, 251, 0.95)'};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.6)' : '#6b7280')};
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    line-height: 1.4;
    transition: border-color 0.2s ease, background 0.2s ease;
    /* $active: subtle static tint only — no zoom/pulse on the box; icon bobs via svg animation */
    ${({ $active, theme }) =>
        $active &&
        css`
            border-color: ${theme.colors.background.default === '#141414' ? 'rgba(134, 239, 172, 0.45)' : '#86efac'};
            background: ${theme.colors.background.default === '#141414' ? 'rgba(34, 197, 94, 0.07)' : 'rgba(240, 253, 244, 0.92)'};
            & .anticon {
                animation: ${kanbanDropIconBobKeyframes} 1s ease-in-out infinite;
            }
        `}
`;

/** Total only: same fill/stroke as that column’s task cards (Figma — not the whole header row). */
export const KanbanColumnCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    font-family: var(--font-family-geist);
    flex-shrink: 0;
    box-sizing: border-box;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2d2d2d' : '#f3f4f6')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.75)' : '#4b5563')};
    border: 1px solid transparent;
    ${({ $variant, theme }) =>
        $variant &&
        css`
            ${() => {
                const { fill, stroke } = kanbanColumnPalette($variant, theme);
                return css`
                    background: ${fill};
                    border: 1px solid ${stroke};
                    color: ${kanbanColumnHeaderTextColor(theme)};
                    box-shadow: none;
                `;
            }}
        `}
`;

/** Column title + total: neutral row; only KanbanColumnCountBadge uses column colors. */
export const KanbanColumnHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    flex-wrap: nowrap;
    flex-shrink: 0;
    box-sizing: border-box;
    width: 100%;
    min-height: 40px;
    padding: 0;
`;

export const KanbanColumnTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    font-family: var(--font-family-geist);
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme }) => kanbanColumnHeaderTextColor(theme)};
`;

/** Count immediately after column heading (not a pill on the right). */
export const KanbanColumnCountInline = styled.span`
    font-size: 14px;
    font-weight: 600;
    font-family: var(--font-family-geist);
    flex-shrink: 0;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#6b7280')};
`;

export const KanbanColumnBody = styled.div`
    flex: 1 1 0%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    /* Scrollbar track + task cards use TaskCardSurface margin-right for card gutter */
    padding: 0 0 8px 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
    ${({ $dragPlaceholder }) =>
        $dragPlaceholder &&
        css`
            overflow: hidden;
            /* flex-start: single flex child (placeholder) must grow to full column height; inner placeholder centers icon */
            justify-content: flex-start;
            align-items: stretch;
            padding: 8px 4px 8px 0;
            gap: 0;
        `}
    ${({ $qaStack }) =>
        $qaStack &&
        css`
            /* QA tab: framer layout + drag wrappers must not collapse; keep vertical rhythm like Sprints. */
            > * {
                flex-shrink: 0;
                width: 100%;
                min-width: 0;
            }
        `}

    @media (max-width: 1024px) {
        ${({ $dragPlaceholder }) =>
        !$dragPlaceholder &&
        css`
                flex: 0 0 auto;
                min-height: 0;
                max-height: none;
                overflow-x: hidden;
                overflow-y: visible;
            `}
    }
`;

export const SprintBoardColumnHeader = styled.div`
    padding: 12px 16px;
    font-weight: 600;
    font-size: 14px;
    color: ${({ $color, theme }) => $color || (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    background: transparent;
    border-radius: 12px 12px 0 0;
`;
/* Milestone header above sprint columns: name, description, status — primary at low opacity */
export const MilestoneHeaderWrap = styled.div`
    flex-shrink: 0;
    padding: 14px 16px;
    margin-bottom: 12px;
    border-radius: 12px;
    background: ${({ theme }) => `color-mix(in srgb, ${theme.colors.primary} 10%, transparent)`};
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
export const MilestoneHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
`;
export const MilestoneHeaderName = styled.div`
    font-size: 16px;
    font-weight: 600;
    line-height: 1.35;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
`;
export const MilestoneHeaderDesc = styled.div`
    font-size: 12px;
    line-height: 1.45;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#6b7280')};
    word-break: break-word;
    & ul, & ol { margin: 0 0 0 16px; padding: 0; }
    & li { margin: 2px 0; }
`;
/** Section label badges (Milestone / Sprint) — professional colors, match header tints */
export const SectionBadgeMilestone = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-family: var(--font-family-geist);
    line-height: 1.2;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(99, 102, 241, 0.35)' : 'rgba(99, 102, 241, 0.2)')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#c7d2fe' : '#4338ca')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.35)')};
`;
export const SectionBadgeSprint = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-family: var(--font-family-geist);
    line-height: 1.2;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(20, 184, 166, 0.3)' : 'rgba(20, 184, 166, 0.18)')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#99f6e4' : '#0f766e')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(20, 184, 166, 0.5)' : 'rgba(20, 184, 166, 0.4)')};
`;
/** Task type badge (flow / overview) — green tint */
export const SectionBadgeTask = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-family: var(--font-family-geist);
    line-height: 1.2;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.18)')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#86efac' : '#15803d')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.4)')};
`;
/** Bug type badge (flow / overview) — red tint */
export const SectionBadgeBug = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-family: var(--font-family-geist);
    line-height: 1.2;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(239, 68, 68, 0.35)' : 'rgba(239, 68, 68, 0.18)')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fca5a5' : '#b91c1c')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(239, 68, 68, 0.5)' : 'rgba(239, 68, 68, 0.4)')};
`;
/** Blocker type badge — orange/amber tint */
export const SectionBadgeBlocker = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.02em;
    font-family: var(--font-family-geist);
    line-height: 1.2;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(245, 158, 11, 0.35)' : 'rgba(245, 158, 11, 0.18)')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fcd34d' : '#b45309')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(245, 158, 11, 0.4)')};
`;
/** Left side of header row: section badge above name (column), minimal gap */
export const ColumnBlockHeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
    flex: 1 1 auto;
`;
/** Wrapper for milestone/sprint block: hover expands description content */
export const ColumnBlockHoverWrap = styled.div`
    position: relative;
    overflow: hidden;
    transition: background-color 0.6s ease;
`;
/** Grid-based expand: 0fr → 1fr so expand and shrink both animate smoothly */
export const MilestoneBlockContent = styled.div`
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 1s cubic-bezier(0.34, 1.56, 0.64, 1);
`;
export const MilestoneBlockContentInner = styled.div`
    min-height: 0;
    overflow: hidden;
`;
/** Grid-based expand for sprint description — smooth in both directions */
export const SprintHeaderExpandable = styled.div`
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 1s cubic-bezier(0.34, 1.56, 0.64, 1);
`;
export const SprintHeaderExpandableInner = styled.div`
    min-height: 0;
    overflow: hidden;
`;
/** Milestone block inside each sprint column — professional muted slate/blue tint */
export const MilestoneBlockInColumn = styled(ColumnBlockHoverWrap)`
    flex-shrink: 0;
    padding: 14px 16px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.06)')};
    border-radius: 12px 12px 0 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.09)')};
    }
    &:hover ${MilestoneBlockContent} {
        grid-template-rows: 1fr;
    }
`;
/* Sprint column: header block — professional teal/slate tint; $noTopRadius when milestone block is above; $dragDisabled shows not-allowed cursor and no hover (full-card dim is via SprintBoardColumn $columnDisabled) */
export const SprintColumnHeaderBlock = styled(ColumnBlockHoverWrap)`
    flex-shrink: 0;
    padding: 14px 16px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(20, 184, 166, 0.08)' : 'rgba(20, 184, 166, 0.07)')};
    border-radius: ${({ $noTopRadius }) => ($noTopRadius ? '0' : '12px 12px 0 0')};
    display: flex;
    flex-direction: column;
    gap: 10px;
    ${({ $dragDisabled }) =>
        $dragDisabled &&
        `
        cursor: not-allowed;
        pointer-events: auto;
    `}
    &:hover {
        background: ${({ theme, $dragDisabled }) =>
        $dragDisabled ? undefined : theme.colors.background.default === '#141414' ? 'rgba(20, 184, 166, 0.12)' : 'rgba(20, 184, 166, 0.1)'};
    }
    &:hover ${SprintHeaderExpandable} {
        grid-template-rows: 1fr;
    }
`;
export const SprintColumnTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;
export const SprintColumnSprintName = styled.div`
    font-size: 15px;
    font-weight: 600;
    line-height: 1.35;
    color: ${({ $color, theme }) => $color || (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    letter-spacing: -0.01em;
`;
export const SprintColumnDates = styled.div`
    font-size: 12px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#6b7280')};
    display: flex;
    align-items: center;
    gap: 6px;
`;
export const SprintColumnProgressWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;
/* Description below progress: 3-line clamp, Read more/less inline (Link style) */
export const SprintColumnDescBlock = styled.div`
    display: ${({ $expanded }) => ($expanded ? 'block' : '-webkit-box')};
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ $expanded }) => ($expanded ? 'unset' : '3')};
    overflow: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
    font-size: 12px;
    line-height: 1.45;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#6b7280')};
    word-break: break-word;
`;
export const ReadMoreLessLink = styled.button`
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    font-size: inherit;
    line-height: inherit;
    font-weight: 600;
    color: #7c3aed;
    cursor: pointer;
    font-family: inherit;
    position: relative;
    display: inline;
    text-decoration: none;
    transition: color 0.25s ease;
    &:hover {
        color: #6d28d9;
    }
    &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background: currentColor;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    }
    &:hover::after {
        transform: scaleX(1);
    }
`;
/* Small Add task button: primary color with light background */
export const AddTaskButtonLight = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 500;
    font-family: var(--font-family-geist);
    border-radius: 6px;
    border: 1px solid
        ${({ theme }) => {
        const hex = theme?.colors?.background?.primary || '#7543DC';
        const h = hex.replace('#', '');
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        return `rgba(${r},${g},${b},0.35)`;
    }};
    background: ${({ theme }) => {
        const hex = theme?.colors?.background?.primary || '#7543DC';
        const h = hex.replace('#', '');
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        return `rgba(${r},${g},${b},0.12)`;
    }};
    color: ${({ theme }) => theme?.colors?.background?.primary || '#7543DC'};
    cursor: pointer;
    transition:
        background 0.2s,
        border-color 0.2s,
        color 0.2s;
    &:hover {
        background: ${({ theme }) => {
        const hex = theme?.colors?.background?.primary || '#7543DC';
        const h = hex.replace('#', '');
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        return `rgba(${r},${g},${b},0.22)`;
    }};
        border-color: ${({ theme }) => {
        const hex = theme?.colors?.background?.primary || '#7543DC';
        const h = hex.replace('#', '');
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        return `rgba(${r},${g},${b},0.5)`;
    }};
    }
    .anticon {
        font-size: 11px;
    }
`;
/** Icon-only add task button: same height as status badge (xs) for alignment in sprint header */
export const AddTaskIconButton = styled(AddTaskButtonLight)`
    width: 30px;
    height: 30px;
    padding: 0;
    min-width: 22px;
    .anticon {
        font-size: 12px;
    }
`;
export const SprintBoardColumnBody = styled.div`
    flex: 1 1 0;
    min-height: 100px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: transparent;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
`;
/** Priority: low = nothing (default), medium = #FFED29, high = red + glow. */
const priorityBorderColor = ($priority, isDark) => {
    if (!$priority) return null;
    const p = String($priority).toLowerCase();
    if (p === 'low') return null;
    if (p === 'medium') return '#FFED29';
    if (p === 'high') return isDark ? '#ff4d4f' : '#ff4d4f';
    return null;
};

const priorityGlow = ($priority, isDark) => {
    if (!$priority || String($priority).toLowerCase() !== 'high') return null;
    return isDark ? '0 0 14px rgba(255, 77, 79, 0.4)' : '0 0 12px rgba(255, 77, 79, 0.35)';
};

/** Footer for incomplete task: circle + "Mark as done" - revealed on card hover. */
/** Top-right overlay: circle (mark as done) - visible on card hover */
const TaskCardMarkOverlay = styled.div`
    position: absolute;
    bottom: 14px;
    right: 14px;
    top: auto;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
`;

/** Top-right when completed: green tick by default; on hover hide tick and show revert-to-todo button. */
export const TaskCardCompletedBadge = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    .task-card-tick {
        transition: opacity 0.2s ease;
    }
    .task-card-revert-btn-wrap {
        position: absolute;
        inset: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease;
    }
    &:hover .task-card-tick {
        opacity: 0;
        pointer-events: none;
    }
    &:hover .task-card-revert-btn-wrap {
        opacity: 1;
        pointer-events: auto;
    }
`;

/** Circle button for revert to todo - same size as mark-as-done, undo icon */
export const TaskCardRevertCircleButton = styled.button`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#666' : '#bbb')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#fff')};
    cursor: pointer;
    transition:
        border-color 0.2s,
        background 0.2s,
        color 0.2s;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#595959')};
    &:hover {
        border-color: #fa8c16;
        background: rgba(250, 140, 22, 0.12);
        color: #fa8c16;
    }
`;

/** Top-right overlay: circle with cross (remove) - visible on hover in submission panel. */
export const TaskCardRemoveOverlay = styled.div`
    position: absolute;
    bottom: 14px;
    right: 14px;
    top: auto;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
`;

/** Circle button with cross icon for remove - same size as TaskCardCircleButton, red on hover. */
export const TaskCardCircleRemoveButton = styled.button`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#666' : '#bbb')};
    background: transparent;
    cursor: pointer;
    transition:
        border-color 0.2s,
        background 0.2s,
        color 0.2s;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#8c8c8c')};
    &:hover {
        border-color: var(--ant-color-error);
        background: rgba(255, 77, 79, 0.12);
        color: var(--ant-color-error);
    }
`;

/** Wrapper for panel section (e.g. sprint header) that reveals circle-remove on hover. */
export const PanelHeaderWithRemoveWrap = styled.div`
    position: relative;
    &:hover ${TaskCardRemoveOverlay} {
        opacity: 1;
        pointer-events: auto;
    }
`;

/** Small priority indicator dot (top-right of task card). Low = gray, medium = orange, high = red. */
const taskCardPriorityDotColor = ($priority, isDark) => {
    const p = String($priority || '').toLowerCase();
    if (p === 'medium') return '#fa8c16';
    if (p === 'high') return '#ff4d4f';
    return isDark ? '#666' : '#bfbfbf';
};

const priorityDotHighGlowKeyframes = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.45); }
    50% { box-shadow: 0 0 0 6px rgba(255, 77, 79, 0); }
`;

export const TaskCardPriorityDot = styled.span`
    position: absolute;
    top: 10px;
    left: 10px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme, $priority }) =>
        taskCardPriorityDotColor($priority, theme?.colors?.background?.default === '#141414')};
    flex-shrink: 0;
    pointer-events: none;
    ${({ $priority }) =>
        String($priority || '').toLowerCase() === 'high'
            ? css`
                  animation: ${priorityDotHighGlowKeyframes} 1.5s ease-in-out infinite;
              `
            : ''}
`;

/** Refined sprint task card shell (reference layout: TID + priority, title, QA banner, description, attachments). */
export const TaskCardSurface = styled.div`
    position: relative;
    padding: 18px 20px;
    border-radius: 16px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#ffffff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#3a3a3a' : '#e5e5e5')};
    box-shadow: ${({ theme, $kanbanColumn }) =>
        $kanbanColumn
            ? 'none'
            : theme.colors.background.default === '#141414'
                ? '0 1px 3px rgba(0,0,0,0.2)'
                : '0 1px 3px rgba(0,0,0,0.06)'};
    display: flex;
    flex-direction: column;
    min-width: 0;
    cursor: ${({ $kanbanGrab }) => ($kanbanGrab ? 'grab' : 'default')};
    transition: ${({ $kanbanColumn, $kanbanMuteHover }) =>
        $kanbanColumn && $kanbanMuteHover
            ? 'none'
            : $kanbanColumn
                ? 'border-color 0.2s ease'
                : 'transform 0.2s ease, box-shadow 0.25s ease, border-color 0.2s ease'};
    ${({ $kanbanGrab }) =>
        $kanbanGrab &&
        css`
            &:active {
                cursor: grabbing;
            }
        `}
    ${({ $kanbanColumn }) =>
        !$kanbanColumn &&
        css`
            &:hover {
                transform: translateY(-2px);
                border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#4a4a4a' : '#d8d8d8')};
                box-shadow: ${({ theme }) =>
                theme.colors.background.default === '#141414'
                    ? '0 6px 16px rgba(0,0,0,0.35)'
                    : '0 6px 20px rgba(0,0,0,0.08)'};
            }
        `}
    &:hover ${TaskCardRemoveOverlay} {
        opacity: 1;
        pointer-events: auto;
    }
    ${({ theme, $kanbanColumn, $kanbanMuteHover }) =>
        $kanbanColumn &&
        css`
            ${() => {
                const { fill, stroke } = kanbanColumnPalette($kanbanColumn, theme);
                return css`
                    background: ${fill};
                    border: 1px solid ${stroke};
                    box-shadow: none;
                    border-radius: 10px;
                    /* Narrower than column so a gutter sits between card and scrollbar (see Kanban column scroll) */
                    width: calc(100% - 10px);
                    max-width: calc(100% - 10px);
                    min-width: 0;
                    margin-right: 10px;
                    align-self: flex-start;
                    flex-shrink: 0;
                    overflow: visible;
                    box-sizing: border-box;
                    /* Jira-style compact card: tighter padding + gap */
                    padding: 10px 12px;
                    gap: 8px;
                    & > * {
                        margin-top: 0 !important;
                        margin-bottom: 0 !important;
                    }
                    ${!$kanbanMuteHover &&
                    css`
                        &:hover {
                            transform: none;
                            box-shadow: none;
                            border-color: ${stroke};
                        }
                    `}
                `;
            }}
        `}
`;

export const TaskCardTopMetaRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ $kanbanColumn }) => ($kanbanColumn ? '8px' : '12px')};
    margin-bottom: ${({ $kanbanColumn }) => ($kanbanColumn ? '6px' : '10px')};
    min-height: 0;
    flex-shrink: 0;
`;

export const TaskCardTaskIdLabel = styled.span`
    font-size: ${({ $kanbanColumn }) => ($kanbanColumn ? '11px' : '12px')};
    font-weight: 500;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    letter-spacing: 0.02em;
    color: ${({ theme, $kanbanColumn }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($kanbanColumn === 'bugs') {
            return isDark ? 'rgba(255,255,255,0.55)' : '#111827';
        }
        return isDark ? 'rgba(255,255,255,0.5)' : '#6b7280';
    }};
`;

const taskCardPillBg = ($priority, isDark) => {
    const p = String($priority || 'low').toLowerCase();
    if (p === 'low') return isDark ? 'rgba(2,106,162,0.22)' : '#E0F2FE';
    if (p === 'medium') return isDark ? 'rgba(234,88,12,0.2)' : '#FFF3E0';
    if (p === 'high') return isDark ? 'rgba(220,38,38,0.22)' : '#FEE2E2';
    return isDark ? '#363636' : '#f3f4f6';
};

const taskCardPillColor = ($priority, isDark) => {
    const p = String($priority || 'low').toLowerCase();
    if (p === 'low') return isDark ? '#38BDF8' : '#026AA2';
    if (p === 'medium') return isDark ? '#FB923C' : '#EA580C';
    if (p === 'high') return isDark ? '#F87171' : '#DC2626';
    return isDark ? 'rgba(255,255,255,0.7)' : '#4b5563';
};

export const TaskCardPriorityPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    text-transform: capitalize;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    background: ${({ theme, $priority }) => taskCardPillBg($priority, theme.colors.background.default === '#141414')};
    color: ${({ theme, $priority }) => taskCardPillColor($priority, theme.colors.background.default === '#141414')};
    border: 1px solid
        ${({ theme, $priority }) => {
        const isDark = theme.colors.background.default === '#141414';
        const p = String($priority || 'low').toLowerCase();
        if (p === 'low') return isDark ? 'rgba(56,189,248,0.35)' : 'rgba(2,106,162,0.22)';
        if (p === 'medium') return isDark ? 'rgba(251,146,60,0.35)' : 'rgba(234,88,12,0.22)';
        if (p === 'high') return isDark ? 'rgba(248,113,113,0.4)' : 'rgba(220,38,38,0.22)';
        return isDark ? '#434343' : '#e5e7eb';
    }};
    flex-shrink: 0;
`;

export const TaskCardPriorityPillDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ theme, $priority }) => taskCardPillColor($priority, theme.colors.background.default === '#141414')};
`;

/** Kanban status pill (completed: green; submitted: purple #E7DEF9 / #7543DC) */
export const TaskCardKanbanStatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    text-transform: capitalize;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    flex-shrink: 0;
    box-sizing: border-box;
    border: none;
    ${({ theme, $variant }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($variant === 'submitted') {
            return css`
                background: ${isDark ? 'rgba(117, 67, 220, 0.22)' : '#E7DEF9'};
                color: ${isDark ? '#c4b5fd' : '#7543DC'};
            `;
        }
        if ($variant === 'bugs') {
            return css`
                background: ${isDark ? 'rgba(251, 146, 60, 0.2)' : '#fdf1ee'};
                color: ${isDark ? '#fdba74' : '#c2410c'};
            `;
        }
        if ($variant === 'returned') {
            return css`
                background: ${isDark ? 'rgba(248, 113, 113, 0.18)' : '#fff1f0'};
                color: ${isDark ? '#fca5a5' : '#dc2626'};
            `;
        }
        if ($variant === 'backlog') {
            return css`
                background: ${isDark ? 'rgba(251, 146, 60, 0.2)' : '#fff7e6'};
                color: ${isDark ? '#fdba74' : '#d46b08'};
            `;
        }
        if ($variant === 'issueOrUpgrade') {
            return css`
                background: ${isDark ? 'rgba(22, 119, 255, 0.2)' : '#e6f4ff'};
                color: ${isDark ? '#40a9ff' : '#0958d9'};
            `;
        }
        return css`
            background: ${isDark ? 'rgba(52, 168, 83, 0.22)' : '#D1FCDC'};
            color: #34a853;
        `;
    }}
`;

/** Column total badge: same shell as status pill; count uses semibold to match header totals. */
export const TaskCardKanbanStatusPillCount = styled(TaskCardKanbanStatusPill)`
    font-weight: 600;
    min-width: 28px;
    justify-content: center;
`;

export const TaskCardKanbanStatusDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ theme, $variant }) => {
        if ($variant === 'submitted') {
            return theme.colors.background.default === '#141414' ? '#c4b5fd' : '#7543DC';
        }
        if ($variant === 'bugs') {
            return theme.colors.background.default === '#141414' ? '#fdba74' : '#ea580c';
        }
        if ($variant === 'returned') {
            return theme.colors.background.default === '#141414' ? '#f87171' : '#dc2626';
        }
        if ($variant === 'backlog') {
            return theme.colors.background.default === '#141414' ? '#fb923c' : '#ea580c';
        }
        if ($variant === 'issueOrUpgrade') {
            return theme.colors.background.default === '#141414' ? '#40a9ff' : '#0958d9';
        }
        return '#34a853';
    }};
`;

export const TaskCardHeadingTitle = styled.div`
    font-size: ${({ $kanbanColumn }) => ($kanbanColumn ? '13px' : '16px')};
    font-weight: ${({ $kanbanColumn }) => ($kanbanColumn ? 600 : 700)};
    line-height: 1.35;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    word-break: break-word;
    margin-bottom: ${({ $kanbanColumn }) => ($kanbanColumn ? '4px' : '8px')};
    flex-shrink: 0;
    color: ${({ theme, $kanbanColumn }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($kanbanColumn === 'bugs') {
            return isDark ? 'rgba(255,255,255,0.95)' : '#000000';
        }
        return isDark ? 'rgba(255,255,255,0.95)' : '#111827';
    }};
    min-width: 0;
    ${({ $lineClamp1 }) =>
        $lineClamp1 &&
        css`
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            overflow: hidden;
            text-overflow: ellipsis;
        `}
`;

export const TaskCardQaReturnRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ff8a80' : '#e57373')};
    margin-bottom: 10px;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    flex-shrink: 0;
`;

export const TaskCardRule = styled.div`
    height: 1px;
    flex-shrink: 0;
    background: ${({ theme, $kanbanColumn }) => {
        if ($kanbanColumn) {
            const { stroke } = kanbanColumnPalette($kanbanColumn, theme);
            return `color-mix(in srgb, ${stroke} 40%, transparent)`;
        }
        return theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    }};
    margin: ${({ $kanbanColumn }) => ($kanbanColumn ? '0' : '0 0 12px 0')};
`;

export const TaskCardDescScroll = styled.div`
    max-height: 200px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
    margin-bottom: 12px;
    min-height: 0;
    background: transparent;
    ${({ $kanbanColumn, $previewLines }) =>
        $kanbanColumn &&
        css`
            flex: 1 1 auto;
            min-height: 0;
            max-height: none;
            margin-bottom: 0;
            padding-right: 0;
            overflow: hidden;
            overflow-y: hidden;
            ${$previewLines != null &&
            Number($previewLines) > 0 &&
            css`
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: ${Number($previewLines)};
                text-overflow: ellipsis;
            `}
        `}
    font-size: 13px;
    line-height: 1.5;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    color: ${({ theme, $kanbanColumn }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($kanbanColumn === 'bugs') {
            return isDark ? 'rgba(255,255,255,0.72)' : '#6b7280';
        }
        return isDark ? 'rgba(255,255,255,0.78)' : '#4b5563';
    }};
    p {
        margin: 4px 0;
        font-size: inherit;
    }
    ul,
    ol {
        margin: 6px 0;
        padding-left: 20px;
    }
    li {
        margin: 2px 0;
    }
    ${({ $kanbanColumn }) =>
        $kanbanColumn &&
        css`
            font-size: 12px;
            line-height: 1.45;
        `}
    ${({ $kanbanColumn, $previewLines }) =>
        $kanbanColumn &&
        $previewLines != null &&
        Number($previewLines) > 0 &&
        css`
            p {
                margin: 0;
            }
            p + p {
                margin-top: 0.35em;
            }
        `}
    ${({ $kanbanColumn }) =>
        $kanbanColumn &&
        css`
            scrollbar-width: none;
            &::-webkit-scrollbar {
                width: 0;
                height: 0;
            }
            &::-webkit-scrollbar-thumb {
                background: transparent;
            }
        `}
    ${({ $kanbanColumn }) =>
        !$kanbanColumn &&
        css`
            scrollbar-width: thin;
            scrollbar-color: ${({ theme }) =>
                theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.25)' : 'rgba(0, 0, 0, 0.25)'}
                transparent;
            &::-webkit-scrollbar {
                width: 6px;
            }
            &::-webkit-scrollbar-thumb {
                border-radius: 3px;
                background: ${({ theme }) =>
                theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.22)' : 'rgba(0, 0, 0, 0.2)'};
            }
        `}
`;

export const TaskCardAttachmentsPill = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: ${({ $kanbanColumn }) => ($kanbanColumn ? '4px 10px' : '6px 12px')};
    border-radius: 9999px;
    font-size: ${({ $kanbanColumn }) => ($kanbanColumn ? '11px' : '12px')};
    font-weight: 500;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    width: fit-content;
    max-width: 100%;
    ${({ theme, $kanbanColumn }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($kanbanColumn === 'completed') {
            return css`
                background: ${isDark ? 'rgba(34, 197, 94, 0.12)' : 'rgba(209, 250, 229, 0.65)'};
                color: ${isDark ? 'rgba(187, 247, 208, 0.95)' : '#14532d'};
                border: 1px solid ${isDark ? 'rgba(74, 222, 128, 0.35)' : '#bbf7d0'};
            `;
        }
        if ($kanbanColumn === 'submitted') {
            return css`
                background: ${isDark ? 'rgba(124, 58, 237, 0.2)' : 'rgba(231, 222, 249, 0.9)'};
                color: ${isDark ? '#e9d5ff' : '#5b21b6'};
                border: 1px solid ${isDark ? 'rgba(167, 139, 250, 0.45)' : '#ddd6fe'};
            `;
        }
        if ($kanbanColumn === 'bugs') {
            return css`
                background: ${isDark ? 'rgba(255,255,255,0.06)' : '#ffffff'};
                color: ${isDark ? 'rgba(255,255,255,0.88)' : '#111827'};
                border: 1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'};
            `;
        }
        return css`
            background: ${isDark ? 'transparent' : '#f3f4f6'};
            color: ${isDark ? 'rgba(255,255,255,0.85)' : '#374151'};
            border: 1px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#e5e7eb'};
        `;
    }}
`;

export const TaskCardRevertFooter = styled.div`
    margin-top: 8px;
    padding-top: 8px;
    flex-shrink: 0;
    border-top: 1px solid
        ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`;

export const TaskCardRevertLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: none;
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#6b7280')};
    cursor: pointer;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    &:hover {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#faad14' : '#d48806')};
    }
`;

/** Nested row on a tinted Kanban task (e.g. bug preview) — white surface, peach border (light). */
export const BoardNestedCard = styled.div`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1a1a1a' : '#ffffff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#4a4a4a' : '#fdba74')};
    border-radius: 10px;
    padding: 10px 12px;
    min-width: 0;
    box-sizing: border-box;
`;

export const BoardNestedCardBugId = styled.div`
    font-size: 13px;
    font-weight: 700;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fb923c' : '#ea580c')};
    margin-bottom: 6px;
`;

export const BoardNestedCardPreview = styled.div`
    font-size: 13px;
    line-height: 1.45;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#6b7280')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const SprintBoardCard = styled.div`
    position: relative;
    padding: 16px 18px;
    border-radius: 10px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#fff')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#383838' : '#eee')};
    box-shadow: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.06)'};
    cursor: default;
    transition:
        transform 0.2s ease,
        box-shadow 0.25s ease,
        border-color 0.2s ease;
    display: flex;
    flex-direction: column;
    min-width: 0;
    &:hover {
        transform: translateY(-2px);
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#4a4a4a' : '#d0d0d0')};
        box-shadow: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? '0 6px 16px rgba(0,0,0,0.35)'
            : '0 6px 20px rgba(0,0,0,0.1)'};
    }
    &:hover ${TaskCardRemoveOverlay} {
        opacity: 1;
        pointer-events: auto;
    }
    &:hover .card-description-wrap {
        grid-template-rows: 1fr;
    }
`;
/** Row for title with optional left icon (green check when done). */
export const SprintBoardCardTitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
    min-height: 0;
`;
export const SprintBoardCardTitle = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    line-height: 1.4;
    word-break: break-word;
`;
/** Green circle with white check - always shown when task is done */
export const TaskCardCheckIcon = styled.span`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #22c55e;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
`;
/** Empty circle button - shown on card hover for incomplete tasks; click to mark done */
export const TaskCardCircleButton = styled.button`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#666' : '#bbb')};
    background: transparent;
    cursor: pointer;
    transition:
        border-color 0.2s,
        background 0.2s;
    padding: 0;
    &:hover {
        border-color: #22c55e;
        background: rgba(34, 197, 94, 0.15);
    }
`;
/** Revert to todo - always visible when task is completed */
export const TaskCardRevertButton = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 500;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f5f5f5')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#595959')};
    cursor: pointer;
    transition:
        background 0.2s,
        border-color 0.2s;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#363636' : '#e8e8e8')};
        border-color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#555' : '#bfbfbf')};
    }
`;
export { TaskCardMarkOverlay };
/** Badge showing priority; same pill shape as StatusBadge (50% radius), with priority colors. */
export const MetaPriorityBadge = styled.span`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 9999px;
    font-size: 14px;
    font-weight: 500;
    text-transform: capitalize;
    letter-spacing: 0;
    background: ${({ theme, $priority }) => {
        const isDark = theme.colors.background.default === '#141414';
        const p = String($priority || '').toLowerCase();
        if (p === 'low') return isDark ? '#262626' : '#f5f5f5';
        if (p === 'medium') return isDark ? 'rgba(255,237,41,0.2)' : 'rgba(255,237,41,0.25)';
        if (p === 'high') return isDark ? 'rgba(255,77,79,0.2)' : 'rgba(255,77,79,0.12)';
        return isDark ? '#262626' : '#f5f5f5';
    }};
    color: ${({ theme, $priority }) => {
        const isDark = theme.colors.background.default === '#141414';
        const p = String($priority || '').toLowerCase();
        if (p === 'low') return isDark ? 'rgba(255,255,255,0.65)' : '#6b7280';
        if (p === 'medium') return isDark ? '#faad14' : '#d48806';
        if (p === 'high') return isDark ? '#ff7875' : '#ff4d4f';
        return isDark ? 'rgba(255,255,255,0.65)' : '#6b7280';
    }};
    border: 1px solid
        ${({ theme, $priority }) => {
        const isDark = theme.colors.background.default === '#141414';
        const p = String($priority || '').toLowerCase();
        if (p === 'low') return isDark ? '#434343' : '#d9d9d9';
        if (p === 'medium') return '#d48806';
        if (p === 'high') return isDark ? '#ff4d4f' : '#ff4d4f';
        return isDark ? '#434343' : '#d9d9d9';
    }};
    font-family: var(--font-family-geist);
`;
export const SprintBoardCardMeta = styled.div`
    font-size: 12px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#6b7280')};
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
`;
export const SprintBoardCardProgress = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
`;
export const SprintBoardCardProgressBar = styled.div`
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#383838' : '#e8e8e8')};
    overflow: hidden;
`;
export const SprintBoardCardProgressFill = styled.div`
    height: 100%;
    border-radius: 3px;
    background: #22c55e;
    width: ${({ $percent }) => Math.min(100, Math.max(0, $percent ?? 0))}%;
    transition: width 0.2s;
`;
export const SprintBoardCardProgressText = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#6b7280')};
    flex-shrink: 0;
`;
export const SprintBoardCardFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid
        ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`;
/** Grid-based expand/collapse on card hover: same as sprint header — smooth 1s both ways. Without .card-description-wrap class, stays expanded (1fr). */
export const CardDescriptionWrap = styled.div`
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 1s cubic-bezier(0.34, 1.56, 0.64, 1);
    &.card-description-wrap {
        grid-template-rows: 0fr;
    }
`;
export const CardDescriptionWrapInner = styled.div`
    min-height: 0;
    overflow: hidden;
`;
export const CardDescriptionInner = styled.div`
    padding-top: 2px;
    padding-bottom: 4px;
    font-size: 12px;
    line-height: 1.5;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.75)' : '#4b5563')};
    word-break: break-word;
    background: transparent;
    ul,
    ol {
        margin: 6px 0;
        padding-left: 20px;
    }
    li {
        margin: 2px 0;
    }
    p {
        margin: 4px 0;
    }
`;
/** See more / See less toggle below description */
export const SeeMoreLessButton = styled.button`
    margin: 0;
    padding: 2px 0 6px;
    border: none;
    background: none;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.background?.primary || '#7c3aed'};
    cursor: pointer;
    font-family: var(--font-family-geist);
    transition:
        color 0.2s,
        opacity 0.2s;
    &:hover {
        opacity: 0.9;
        text-decoration: underline;
    }
`;
/** Generic small link-style button for card actions (e.g. Mark as done) */
export const InlineActionLink = styled.button`
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    font-size: 12px;
    font-weight: 500;
    color: #22c55e;
    cursor: pointer;
    text-decoration: none;
    font-family: var(--font-family-geist);
    &:hover {
        text-decoration: underline;
        color: #16a34a;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
export const SprintBoardCardAssignee = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;
export const SprintBoardCardAvatar = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ $bg, theme }) => $bg || (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
    color: ${({ $color, theme }) => $color || (theme.colors.background.default === '#141414' ? '#fff' : '#333')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
`;

/** Overlapping avatar stack (e.g. assignees). First item no margin; rest overlap left. */
export const AvatarStackWrap = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    & > * {
        margin-left: -10px;
    }
    & > *:first-child {
        margin-left: 0;
    }
`;
/** Single circle in stack: border matches background so overlap looks clean. */
export const AvatarStackCircle = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#1f1f1f' : '#fff')};
    background: ${({ $bg, theme }) => $bg || (theme?.colors?.background?.default === '#141414' ? '#434343' : '#d9d9d9')};
    color: ${({ $color, theme }) => $color || (theme?.colors?.background?.default === '#141414' ? '#fff' : '#333')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
    box-sizing: border-box;
`;
/** Overflow circle (e.g. +12) in avatar stack. */
export const AvatarStackOverflow = styled(AvatarStackCircle)`
    background: #8b7fc7;
    color: #fff;
    border-color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#1f1f1f' : '#fff')};
    font-size: 12px;
    font-weight: 600;
`;

/* Sprint section: main section = Sprint, inside = list of tasks */
export const SprintSectionCard = styled(ContentCard)`
    margin-bottom: 20px;
    &:last-child {
        margin-bottom: 0;
    }
`;
export const SprintSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
`;
export const SprintSectionTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#1a1a1a')};
    font-family: var(--font-family-geist);
`;
export const SprintSectionDescription = styled.div`
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#6b7280')};
    margin-bottom: 12px;
    line-height: 1.5;
`;
export const SprintSectionMeta = styled.div`
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#8c8c8c')};
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
`;
export const SprintSectionProgressWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
`;
export const SprintSectionProgressBar = styled.div`
    flex: 1;
    height: 8px;
    border-radius: 4px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#383838' : '#e8e8e8')};
    overflow: hidden;
`;
export const SprintSectionProgressFill = styled.div`
    height: 100%;
    border-radius: 4px;
    background: #22c55e;
    width: ${({ $percent }) => Math.min(100, Math.max(0, $percent ?? 0))}%;
    transition: width 0.2s;
`;
export const SprintSectionProgressText = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.8)' : '#6b7280')};
    flex-shrink: 0;
`;
export const SprintTaskListTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#374151')};
    margin-bottom: 10px;
`;
export const SprintTaskList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
export const SprintTaskItem = styled.div`
    padding: 10px 14px;
    border-radius: 8px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.04)' : '#fafafa'};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#383838' : '#eee')};
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#1a1a1a')};
`;
/** Generic drop zone area (e.g. for drag-and-drop submission panels). */
export const DropZoneArea = styled.div`
    min-height: 80px;
    border-radius: 12px;
    border: 2px dashed
        ${({ $active, theme }) =>
        $active
            ? 'var(--ant-color-primary)'
            : theme.colors.background.default === '#141414'
                ? '#434343'
                : '#d9d9d9'};
    background: ${({ $active, theme }) =>
        $active
            ? theme.colors.background.default === '#141414'
                ? 'rgba(22, 119, 255, 0.08)'
                : 'rgba(22, 119, 255, 0.04)'
            : theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.02)'
                : '#fafafa'};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    transition:
        border-color 0.2s,
        background 0.2s;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    font-size: 14px;
`;

/** Wrapper for disabled/dimmed state (e.g. submitted items that cannot be dragged again). */
export const DisabledDimWrap = styled.div`
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
    transition: opacity 0.2s ease;
`;

/** Visual-only dull styling when milestone is inactive – clearly different opacity so inactive milestone cards stand out. */
export const InactiveMilestoneWrap = styled.div`
    opacity: ${({ $inactive }) => ($inactive ? 0.52 : 1)};
    filter: ${({ $inactive }) => ($inactive ? 'saturate(0.75)' : 'none')};
    transition: opacity 0.2s ease, filter 0.2s ease;
`;

/** Generic right-side split panel. Use $expanded for hover: true = 50% width, false = 25%. */
export const SplitPanelRight = styled.div`
    width: ${({ $expanded, $width }) => {
        if ($width != null) return `${$width}px`;
        return $expanded ? '50%' : '25%';
    }};
    flex-shrink: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border-left: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1a1a1a' : '#fafafa')};
    padding: 16px;
    overflow: hidden;
    border-radius: 12px;
    transition: width 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    ${({ $dropTargetActive, $dropTargetHover, theme }) => {
        const isDark = theme.colors?.background?.default === '#141414';
        const borderColor = isDark ? 'rgba(20, 184, 166, 0.5)' : 'rgba(20, 184, 166, 0.55)';
        const glowLight = isDark ? 'rgba(20, 184, 166, 0.18)' : 'rgba(20, 184, 166, 0.22)';
        const glowSoft = isDark ? 'rgba(20, 184, 166, 0.1)' : 'rgba(20, 184, 166, 0.14)';
        if ($dropTargetHover) {
            return `
                border: 2px solid ${borderColor};
                box-shadow: 0 0 0 1px ${borderColor}, 0 0 28px ${glowLight}, 0 0 56px ${glowSoft};
            `;
        }
        if ($dropTargetActive) {
            return `
                border: 2px solid ${borderColor};
                box-shadow: 0 0 20px ${glowLight}, 0 0 44px ${glowSoft};
            `;
        }
        return '';
    }}
`;

/** Narrow column for Kanban inside a panel (same look, smaller width). */
export const NarrowBoardColumn = styled(SprintBoardColumn)`
    flex: 0 0 220px;
    min-width: 220px;
    max-width: 220px;
    max-height: 100%;
`;

/** Wrapper that reveals a delete action on hover (e.g. sprint header or task card). */
export const HoverRevealWrap = styled.div`
    position: relative;
    &:hover .hover-reveal-action {
        opacity: 1;
        transform: translateY(-50%) scale(1);
    }
`;

/** Small delete icon button, revealed on parent hover. Professional look with animation. */
export const HoverRevealDeleteBtn = styled.button`
    opacity: 0;
    transform: translateY(-50%) scale(0.9);
    transition:
        opacity 0.2s ease,
        transform 0.2s ease,
        color 0.2s ease,
        background 0.2s ease;
    position: absolute;
    top: 50%;
    right: 8px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#8c8c8c')};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &.hover-reveal-action:hover {
        color: var(--ant-color-error);
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,77,79,0.12)' : 'rgba(255,77,79,0.08)'};
        transform: translateY(-50%) scale(1.08);
    }
`;

export {
    BoardTabRoot,
    BoardKanbanBleedRight,
    BoardKanbanBleedRightFlushNarrow,
    BoardKanbanBoardShell,
    BoardKanbanFetchingStrip,
    BoardKanbanShimmerRoot,
    BoardKanbanShimmerColumn,
    BoardKanbanShimmerBlock,
    KANBAN_DESKTOP_COLUMN_MIN_PX,
    kanbanDesktopHeaderCellFlexStyle,
    kanbanDesktopBoardRowInnerStyle,
    kanbanDesktopBoardHeaderRowInnerStyle,
    kanbanDesktopFixedHeaderCellStyle,
    kanbanDesktopFixedBoardRowInnerStyle,
    kanbanDesktopFixedBoardHeaderRowInnerStyle,
    SprintBoardWrap,
    SprintsTabLayout,
    SprintBoardHeader,
    SprintBoardSearch,
    SprintBoardToolbar,
    SprintBoardColumns,
    SprintHeadersRow,
    SprintHeaderCell,
    SprintBoardColumn,
    BoardPageTitle,
    BoardPageSubtitle,
    BoardPillTabRow,
    BoardPillTabLabel,
    BoardPillTab,
    BoardCollapseSectionHeading,
    BoardDetailBlockTitle,
    BoardDetailBlockBody,
    BoardDetailKeyLabel,
    BoardDetailMetaStack,
    BoardDetailMetaRow,
    BoardDetailMetaValue,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabSprintName,
    BoardUnderlineTabLabel,
    BoardUnderlineTab,
    BoardUnderlineTabPercent,
    BoardUnderlineTabCountBadge,
} from '@/pages/inside/modules/projects/detail/projectTabStyles';

export const AvatarStackContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;

    .avatar-member {
        display: inline-flex;
    }

    /* On large screens, hide the 11th team member and beyond */
    .avatar-member:nth-child(n+11) {
        display: none !important;
    }
    
    /* On medium screens, collapse and show up to 5 team members */
    @media (max-width: 1199px) {
        .avatar-member:nth-child(n+6) {
            display: none !important;
        }
    }

    /* On small screens, collapse and show up to 3 team members */
    @media (max-width: 768px) {
        .avatar-member:nth-child(n+4) {
            display: none !important;
        }
    }
`;

export const SettingsModulesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    width: 100%;
`;

export const SettingsModuleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${rgbaFromHex(colors.neutral[100], 0.35)};
    border: none;
    transition: all 0.15s ease;
    
    &:hover {
        background: ${rgbaFromHex(colors.primary[50], 0.8)};
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
    }
    
    .toggle-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        flex-shrink: 0;
        font-size: 13px;
        transition: all 0.2s ease;
    }
    
    .toggle-content {
        flex: 1;
        min-width: 0;
        
        .toggle-label {
            font-size: 12px;
            font-weight: 600;
            color: ${colors.neutral[800]};
            display: flex;
            align-items: center;
            gap: 4px;
        }
        
        .toggle-desc {
            font-size: 10px;
            color: ${colors.neutral[400]};
            line-height: 1.3;
            margin-top: 1px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
`;

export const InteractiveClientCard = styled.div`
    background: ${colors.neutral[50]};
    border: 1px solid ${colors.neutral[200]};
    border-radius: 12px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 74px;
    box-sizing: border-box;
    cursor: ${props => props.$canUpdate ? 'pointer' : 'default'};
    transition: all 0.2s ease-in-out;

    &:hover {
        background: ${props => props.$canUpdate ? rgbaFromHex(colors.primary[50], 0.6) : colors.neutral[50]};
        border-color: ${props => props.$canUpdate ? colors.primary[300] : colors.neutral[200]};
        box-shadow: ${props => props.$canUpdate ? `0 4px 12px ${rgbaFromHex(colors.primary[600], 0.05)}` : 'none'};
    }
`;

export const TemplateList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px;
    max-height: 55vh;
    overflow-y: auto;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: ${colors.neutral[200]}; border-radius: 10px; }
`;

export const TemplateCard = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    border: 1px solid ${props => props.$selected ? colors.primary[600] : colors.neutral[200]};
    background: ${props => props.$selected ? colors.primary[50] : colors.neutral[0]};
    border-radius: 16px;
    padding: 16px 20px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    position: relative;
    box-shadow: ${props => props.$selected ? `0 0 0 1px ${colors.primary[600]}` : 'none'};

    &:hover {
        border-color: ${colors.primary[400]};
        background: ${props => props.$selected ? colors.primary[50] : colors.neutral[50]};
    }

    .illustration-box {
        width: 80px; height: 64px; background: #fff; border-radius: 12px; display: flex; 
        align-items: center; justify-content: center; flex-shrink: 0; border: 1px solid ${colors.neutral[100]};
        overflow: hidden; svg { width: 100%; height: 100%; }
    }

    .content-box {
        flex: 1;
        .name { font-weight: 700; font-size: 16px; color: ${colors.neutral[900]}; margin-bottom: 2px; }
        .description { font-size: 13px; color: ${colors.neutral[500]}; line-height: 1.4; }
    }

    .meta-box { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
    .check-icon { color: ${colors.primary[600]}; font-size: 20px; visibility: ${props => props.$selected ? 'visible' : 'hidden'}; }
`;

